/**
 * CR518: Snippet Preview Drawer
 * Shows source page with bbox overlay (Pass A / Pass B / Final) + optional cropped snippet.
 * Addendum: Delta badge comparing Pass A vs Pass B bbox quality.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Copy, ZoomIn, Image as ImageIcon, Crop, X, Layers, Target, ArrowDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { bboxArea, bboxDelta, classifyDelta, type BboxNorm, type BboxDelta, type DeltaClass } from "@/lib/snippets/bboxDelta";

interface SnippetData {
  staging_item_id: string | null;
  deal_id: string | null;
  page_number: number | null;
  snippet_status: string | null;
  bbox_norm: BboxNorm | null;
  bbox_confidence: number | null;
  bbox_score: number | null;
  bbox_locator_pass: string | null;
  source_page_bucket: string | null;
  source_page_path: string | null;
  snippet_asset_path: string | null;
  merchantName?: string;
  // CR518: Pass A/B diagnostics
  pass_a_bbox_norm?: BboxNorm | null;
  pass_b?: {
    attempted?: boolean;
    used?: boolean;
    // CR551: Deterministic decision taxonomy
    decision?: "refined" | "fallback_to_pass_a" | "internal_error" | "skipped" | null;
    skip_reason?: string | null;
    // CR558: Trace fields
    ai_invoked?: boolean;
    ai_skip_reason?: string | null;
    build?: { function?: string; function_name?: string; version?: string; git_sha?: string; deployment_id?: string } | null;
    confidence?: number | null;
    matched_anchors?: string[];
    reason?: string | null;
    bbox_norm?: BboxNorm | null;
    // CR528/CR530 diagnostics
    error?: string | null;
    parse_mode?: "direct" | "fenced" | "substring" | "failed" | null;
    raw_preview?: string | null;
    bbox_raw?: Record<string, any> | null;
    // CR553: Pass B input provenance
    input_source?: "roi_from_pass_a" | "full_page_bypass" | null;
    input_bbox_norm?: BboxNorm | null;
    roi_px?: { w: number; h: number } | null;
    roi_asset_path?: string | null;
    // CR591: Clamp diagnostics
    clamped?: boolean;
    overflow_max?: number;
    fail_reason?: string | null;
    // CR554: OCR availability for Pass B
    ocr_available?: boolean;
    // CR555: Anchor gate discrepancy
    anchor_gate?: {
      ocr_anchor_passed?: boolean;
      model_claimed_missing?: boolean;
      must_hit_ratio?: number;
      should_hit_ratio?: number;
      must_hit_count?: number;
      must_hit_total?: number;
      should_hit_count?: number;
      should_hit_total?: number;
      anchor_token_hits?: number;
    } | null;
    model_anchor_false_negative?: boolean;
    // CR557: Input invariant guardrail
    input_invariant?: { ok: boolean; failures: string[]; areaRatio: number | null } | null;
    page_px?: { w: number; h: number } | null;
    // CR557: Structured sub-objects (from cropper_pass_b canonical payload)
    pack?: {
      prompt_pack_id?: string | null;
      prompt_pack_snapshot_id?: string | null;
      prompt_pack_name?: string | null;
      prompt_pack_version_label?: string | null;
    } | null;
    input?: {
      source?: string | null;
      asset_path?: string | null;
      px?: { w: number; h: number } | null;
      page_px?: { w: number; h: number } | null;
      area_ratio?: number | null;
    } | null;
    invariant?: {
      ok?: boolean;
      failures?: string[];
      flags?: Record<string, boolean>;
    } | null;
    /** CR560: Model's explicit decision — "refine" | "no_change" */
    model_decision?: "refine" | "no_change" | null;
    /** CR562: ROI-local clamp diagnostics */
    bbox_norm_raw?: BboxNorm | null;
    bbox_norm_clamped?: BboxNorm | null;
    bbox_clamp_applied?: boolean;
    bbox_max_overflow?: number | null;
    /** CR555-P4: ROI evidence diagnostics */
    roi_evidence?: {
      roi_ocr_text_200?: string;
      roi_ocr_char_count?: number;
      roi_evidence_score?: number;
      roi_evidence_hits_brand?: number;
      roi_evidence_hits_value?: number;
      roi_evidence_hits_mechanic?: number;
      roi_evidence_total_hits?: number;
      roi_evidence_tokens_checked?: number;
      roi_matched_tokens?: string[];
    } | null;
    /** CR557: ROI source provenance */
    roi_source?: "pass_a_bbox" | "legacy_roi_fallback" | null;
    /** CR557: Original Pass A bbox */
    pass_a_bbox_norm?: BboxNorm | null;
    /** CR557: ROI OCR text always available (even on failure) */
    roi_ocr_text_200?: string | null;
    roi_ocr_source?: string | null;
    /** CR551B: Deep diagnostics */
    failure_stage?: string | null;
    failure_code?: string | null;
    failure_detail?: string | null;
    /** CR552: Geometry sanity */
    area_shrink_ratio?: number | null;
    iou_vs_pass_a?: number | null;
    roi_quality?: { roi_area_ratio?: number; roi_pixel_area?: number; roi_text_char_count?: number; roi_text_density?: number } | null;
    evidence_snapshot?: { anchor_tokens?: string[]; value_tokens?: string[]; mechanic_tokens?: string[]; total_tokens_checked?: number; evidence_gate_passed?: boolean } | null;
    model_telemetry?: { model_name?: string | null; model_temperature?: number | null; request_token_count?: number | null; response_token_count?: number | null; model_error_code?: string | null; model_gate_passed?: boolean } | null;
    refinement_outcome?: string | null;
    pass_a_ocr_sample?: string | null;
    pass_b_ocr_sample?: string | null;
    bbox_source_key?: string | null;
    model_coord_space?: string | null;
    subset_check_parent?: string | null;
    roi_expansion_mode?: string | null;
    roi_margin_used?: number | null;
    roi_area_ratio?: number | null;
    // CR554: Mic Check debug stamp
    debug_stamp?: string | null;
    // CR554: Pass B Observatory trace
    pass_b_trace?: {
      ai_invoked: boolean;
      decision: string;
      skip_reason?: string;
      gate_reason?: string;
      stages: Array<{ key: string; label: string; status: string; detail?: string }>;
    } | null;
    // CR554: Pass B coordinate sanity snapshot
    pass_b_coords?: {
      input_space: string;
      proposal_bbox_norm: BboxNorm | null;
      crop_bbox_norm?: BboxNorm | null;
      crop_image_source: string;
    } | null;
    // CR556: Structured gate snapshot
    pass_b_gate?: {
      inputs?: {
        evidence_score_normalized?: number | null;
        evidence_total_hits?: number | null;
        min_evidence_threshold?: number | null;
        bbox_height_ratio?: number | null;
        min_bbox_height_ratio?: number | null;
        candidate_index?: number | null;
        max_candidates_per_batch?: number | null;
        in_crop_queue?: boolean;
      };
      decision?: string | null;
      detail?: string | null;
    } | null;
  } | null;
  // CR559: Bbox audit trail — proves which bbox each stage used
  bbox_audit_trail?: {
    input_bbox_norm?: BboxNorm | null;
    model_bbox_norm?: BboxNorm | null;
    evaluated_bbox_norm?: BboxNorm | null;
    final_bbox_norm?: BboxNorm | null;
    roi_bbox_norm?: BboxNorm | null;
    pass_a_bbox_norm?: BboxNorm | null;
    final_source?: string | null;
  } | null;
  // CR554: Root-level debug stamp
  debug_stamp?: string | null;
  // CR557: Canonical cropper_pass_b (same object as pass_b)
  cropper_pass_b?: SnippetData['pass_b'];
  // CR557: Top-level jet plane guardrail flag
  pass_b_jet_plane_guardrail_tripped?: boolean | null;
  cropper_used?: boolean;
  final_bbox_source?: "pass_a" | "pass_b" | "locator" | "cropper" | null;
  // CR520: Evidence scoring
  evidence_score?: number | null;
  evidence_hits?: {
    brand?: number;
    value?: number;
    mechanic?: number;
    total?: number;
    tokens_checked?: number;
  } | null;
  matched_evidence_tokens?: string[] | null;
  confidence_final?: number | null;
  evidence_pass_a?: { score?: number; hits?: number } | null;
  // CR534: Effective thresholds from diagnostics
  min_cropper_confidence_used?: number | null;
  min_locator_confidence_used?: number | null;
  // CR520/CR563: Pass A diagnostics
  render_source?: string | null;
  locator_reason?: string | null;
  fallback_source?: string | null;
  // CR614: Deterministic fallback fields
  fallback_strategy?: string | null;
  cr614_original_status?: string | null;
  needs_review?: boolean | null;
  // CR575: bbox_hint diagnostic only (no decision influence)
  bbox_hint_present?: boolean | null;
  // CR574: Image grounding + box OCR diagnostics
  has_image_sent?: boolean | null;
  ai_payload_has_image?: boolean | null;
  box_ocr_text_200?: string | null;
  box_must_hit_count?: number | null;
  box_should_hit_count?: number | null;
  box_anchor_hits?: string[] | null;
  fallback_policy?: 'strict' | 'lenient' | null;
  // CR574: Anchor gate diagnostics
  locator_anchor_must_hits?: string[] | null;
  locator_anchor_should_hits?: string[] | null;
  locator_bbox_ocr_excerpt?: string | null;
  anchor_gate_result?: string | null;
  anchor_gate_reject_reason?: string | null;
  must_hit_tokens_used?: string[] | null;
  should_hit_tokens_used?: string[] | null;
  // CR575: Locator proposed metrics
  locator_proposed_bbox_norm?: BboxNorm | null;
  locator_proposed_area_ratio?: number | null;
  locator_proposed_confidence?: number | null;
  locator_proposed_quality_hint?: string | null;
  anchor_gate_must_hit_count?: number | null;
  anchor_gate_should_hit_count?: number | null;
  anchor_gate_must_hit_required?: number | null;
  anchor_gate_anchor_hits?: string[] | null;
  // CR577: Proposal box OCR diagnostics
  proposal_box_must_hit_count?: number | null;
  proposal_box_must_hit_total?: number | null;
  proposal_box_should_hit_count?: number | null;
  proposal_box_anchor_hits?: string[] | null;
  proposal_box_missing_must?: string[] | null;
  proposal_box_missing_should?: string[] | null;
  proposal_box_ocr_text_200?: string | null;
  anchor_gate_threshold_min_must?: number | null;
  anchor_gate_threshold_min_total?: number | null;
  // CR578: Core/support diagnostics
  proposal_core_hits?: number | null;
  proposal_support_hits?: number | null;
  proposal_core_total?: number | null;
  anchor_gate_rule_version?: string | null;
  // CR580: Per-box OCR diagnostics
  box_ocr_engine?: string | null;
  box_ocr_status?: string | null;
  box_ocr_error?: string | null;
  box_ocr_duration_ms?: number | null;
  // CR580: OCR page mismatch diagnostics
  ocr_page_mismatch_detected?: boolean | null;
  ocr_page_merchant_hint?: { deal_merchant_source_id?: string | null; ocr_source_merchant_id?: string | null } | null;
  // CR581: OCR sanity diagnostics
  ocr_sanity_status?: string | null;
  ocr_sanity_reason?: string | null;
  // CR582: Page OCR source diagnostics
  ocr_page_source?: string | null;
  ocr_page_text_len?: number | null;
  ocr_page_excerpt_200?: string | null;
  // CR583: Box OCR sanity flag
  ocr_box_sanity_status?: string | null;
  ocr_box_sanity_reason?: string | null;
  // CR583: Anchor token strategy
  anchor_token_strategy_version?: string | null;
  // CR585: Truth override
  truth_override_source?: string | null;
  batch_run_id?: string | null;
  // CR556: Trim diagnostics
  trim_applied?: boolean | null;
  trim_reason?: string | null;
  trim_bbox_norm?: BboxNorm | null;
  trim_removed_tokens_sample?: string[] | null;
  // CR557: Panel expansion diagnostics
  pass_a_panel_height_initial?: number | null;
  pass_a_panel_height_final?: number | null;
  pass_a_expansion_mode?: string | null;
  pass_a_expansion_applied?: boolean | null;
  pass_a_conditions_detected?: boolean | null;
  pass_a_conditions_band_text_200?: string | null;
  // CR587: Anchor suggestions
  anchor_suggestions?: {
    keep_must?: string[];
    keep_should?: string[];
    demote_must?: string[];
    new_from_box?: string[];
    debug_sample?: {
      box_ocr_text_200?: string | null;
      box_anchor_hits?: string[];
      box_missing_must?: string[];
    };
  } | null;
  // CR555-P5: High-evidence override diagnostics
  pass_b_roi_evidence_score?: number | null;
  pass_b_confidence_override_reason?: string | null;
  pass_b_fail_reason?: string | null;
  // CR556: Pass B OCR text + regression diagnostics
  pass_b_ocr_text_200?: string | null;
  pass_b_ocr_hit_count?: number | null;
  pass_b_ocr_source?: string | null;
  pass_b_regression?: {
    gate_mode?: string;
    regressed?: boolean;
    allowed_despite_regression?: boolean;
    subset_of_a?: boolean;
    conf_b?: number | null;
    score_a?: number | null;
    score_b?: number | null;
    score_delta?: number | null;
    reason?: string;
  } | null;
  // CR558: Pass A Debug — structured primary vs fallback telemetry
  pass_a_debug?: {
    primary?: {
      strategy?: string;
      status?: string;
      decision?: string;
      reason?: string | null;
      bboxNorm?: BboxNorm | null;
      anchorHits?: number | null;
      evidenceScore?: number | null;
      confidence?: number | null;
      mustHitSatisfied?: boolean | null;
      shouldHitCount?: number | null;
      matchedAnchors?: string[] | null;
      ocrHintUsed?: boolean | null;
      ocrHintSource?: string | null;
    };
    fallback?: {
      strategy?: string;
      status?: string;
      decision?: string;
      reason?: string | null;
      bboxNorm?: BboxNorm | null;
      anchorHits?: number | null;
      evidenceScore?: number | null;
      confidence?: number | null;
      mustHitSatisfied?: boolean | null;
      shouldHitCount?: number | null;
      matchedAnchors?: string[] | null;
      ocrHintUsed?: boolean | null;
      ocrHintSource?: string | null;
    } | null;
    finalSource?: 'primary' | 'fallback';
    fallbackTrigger?: string | null;
  } | null;
  pass_a_primary_bbox_norm?: BboxNorm | null;
  pass_a_primary_reason?: string | null;
  pass_a_primary_evidence?: number | null;
  pass_a_primary_anchor_hits?: number | null;
  pass_a_primary_must_hit_satisfied?: boolean | null;
  pass_a_fallback_trigger?: string | null;
  pass_a_fallback_quadrant?: string | null;
  pass_a_fallback_bbox_norm?: BboxNorm | null;
  pass_a_anchor_gate?: {
    gate_status?: 'pass' | 'fail' | 'skipped';
    fail_code?: string | null;
    must_hit_tokens?: string[];
    missing_must_hit_tokens?: string[];
  } | null;
}

interface Props {
  snippet: SnippetData | null;
  open: boolean;
  onClose: () => void;
  debugView?: boolean; // CR579: Proposal vs Final debug toggle
}

// In-memory cache for signed URLs
const urlCache = new Map<string, { sourceUrl: string; snippetUrl: string | null; expires: number }>();

function cacheKey(s: SnippetData): string {
  return `${s.source_page_bucket}:${s.source_page_path}:${s.snippet_asset_path ?? ""}`;
}

export function SnippetPreviewDrawer({ snippet, open, onClose, debugView = false }: Props) {
  const { toast } = useToast();
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [snippetUrl, setSnippetUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [showPassA, setShowPassA] = useState(false);
  const [showPassB, setShowPassB] = useState(false);
  const [showFinal, setShowFinal] = useState(true);
  const [showProposal, setShowProposal] = useState(true); // CR577: ghost bbox toggle
  const [lockingTruth, setLockingTruth] = useState(false); // CR585
  const [truthLocked, setTruthLocked] = useState(false); // CR585: local indicator after lock
  const [roiPreviewUrl, setRoiPreviewUrl] = useState<string | null>(null); // CR553: Pass B ROI preview
  const [roiPreviewOpen, setRoiPreviewOpen] = useState(false); // CR553: ROI preview expanded
  const bboxRef = useRef<HTMLDivElement>(null);

  const fetchUrls = useCallback(async (s: SnippetData) => {
    if (!s.source_page_bucket || !s.source_page_path) {
      setUrlError("No source page path available.");
      return;
    }

    const key = cacheKey(s);
    const cached = urlCache.get(key);
    if (cached && cached.expires > Date.now()) {
      setSourceUrl(cached.sourceUrl);
      setSnippetUrl(cached.snippetUrl);
      return;
    }

    setUrlLoading(true);
    setUrlError(null);

    const { data, error } = await supabase.functions.invoke("admin-get-snippet-preview-urls", {
      body: {
        source_page_bucket: s.source_page_bucket,
        source_page_path: s.source_page_path,
        snippet_asset_bucket: s.snippet_asset_path ? "flyer-snippets" : undefined,
        snippet_asset_path: s.snippet_asset_path ?? undefined,
      },
    });

    setUrlLoading(false);

    if (error) {
      setUrlError(`Network error: ${(error as any).message ?? String(error)}`);
      return;
    }

    if (data?.ok === false) {
      setUrlError(`${data.error_code}: ${data.message}`);
      return;
    }

    const srcUrl = data?.source_page_signed_url ?? null;
    const snipUrl = data?.snippet_signed_url ?? null;
    setSourceUrl(srcUrl);
    setSnippetUrl(snipUrl);

    if (srcUrl) {
      urlCache.set(key, {
        sourceUrl: srcUrl,
        snippetUrl: snipUrl,
        expires: Date.now() + 50 * 60 * 1000,
      });
    }
  }, []);

  useEffect(() => {
    if (open && snippet) {
      setSourceUrl(null);
      setSnippetUrl(null);
      setUrlError(null);
      setZoomed(false);
      setShowPassA(false);
      setShowPassB(false);
      setShowFinal(true);
      setShowProposal(debugView); // CR579: auto-enable proposal overlay in debug mode
      setTruthLocked(false); // CR585: reset on open
      setLockingTruth(false);
      setRoiPreviewUrl(null); // CR553: reset ROI preview
      setRoiPreviewOpen(false);
      fetchUrls(snippet);

      // CR553: Fetch signed URL for Pass B ROI image if available
      if (snippet.pass_b?.roi_asset_path) {
        supabase.functions.invoke("admin-get-snippet-preview-urls", {
          body: {
            source_page_bucket: snippet.source_page_bucket || "flyer-snippets",
            source_page_path: snippet.pass_b.roi_asset_path,
          },
        }).then(({ data }) => {
          if (data?.ok && data.source_page_signed_url) {
            setRoiPreviewUrl(data.source_page_signed_url);
          }
        }).catch(() => { /* non-fatal */ });
      }
    }
  }, [open, snippet, fetchUrls, debugView]);

  // CR585: Lock proposal as truth bbox
  const handleLockTruth = useCallback(async () => {
    if (!snippet?.staging_item_id || !snippet?.batch_run_id) return;
    const bboxToLock = snippet.locator_proposed_bbox_norm ?? snippet.bbox_norm;
    if (!bboxToLock) return;
    setLockingTruth(true);
    try {
      const { error } = await (supabase as any)
        .from('flyer_snippet_truth_bboxes')
        .upsert({
          batch_run_id: snippet.batch_run_id,
          staging_item_id: snippet.staging_item_id,
          page_number: snippet.page_number ?? 0,
          bbox_norm: bboxToLock,
          source: 'admin_manual',
        }, { onConflict: 'batch_run_id,staging_item_id' });
      if (error) throw error;
      setTruthLocked(true);
      toast({ title: "Truth bbox locked", description: "Future S1.4 runs will use this bbox directly." });
    } catch (err: any) {
      toast({ title: "Failed to lock truth", description: err.message ?? String(err), variant: "destructive" });
    } finally {
      setLockingTruth(false);
    }
  }, [snippet, toast]);

  const bbox = snippet?.bbox_norm as BboxNorm | null;
  const passABbox = snippet?.pass_a_bbox_norm as BboxNorm | null;
  const passBBbox = snippet?.pass_b?.bbox_norm as BboxNorm | null;
  const proposedBbox = snippet?.locator_proposed_bbox_norm as BboxNorm | null;
  const hasMultiplePasses = !!(passABbox || passBBbox);

  // CR577: Show ghost only when proposed differs from final (or final is absent)
  const proposedDiffersFromFinal = useMemo(() => {
    if (!proposedBbox) return false;
    if (!bbox) return true; // no final → always show ghost
    const eps = 0.005;
    return (
      Math.abs(proposedBbox.x - bbox.x) > eps ||
      Math.abs(proposedBbox.y - bbox.y) > eps ||
      Math.abs(proposedBbox.width - bbox.width) > eps ||
      Math.abs(proposedBbox.height - bbox.height) > eps
    );
  }, [proposedBbox, bbox]);

  // Delta badge: compare Pass A vs Pass B
  const delta = useMemo(() => {
    if (!passABbox || !passBBbox) return null;
    return bboxDelta(passABbox, passBBbox);
  }, [passABbox, passBBbox]);

  const deltaClass = useMemo(() => {
    if (!delta) return null;
    return classifyDelta(delta, !!snippet?.cropper_used);
  }, [delta, snippet?.cropper_used]);

  const handleZoom = () => {
    setZoomed(true);
    setTimeout(() => {
      bboxRef.current?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }, 100);
  };

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: `${label} copied` });
  };

  if (!snippet) return null;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0">
        <SheetHeader className="p-4 pb-2 border-b">
          <SheetTitle className="text-base flex items-center gap-2 flex-wrap">
            <span>{snippet.merchantName || snippet.deal_id?.slice(0, 8) || "Snippet"}</span>
            <Badge variant="outline" className="text-xs">Page {snippet.page_number ?? "?"}</Badge>
            <StatusChip status={snippet.snippet_status} />
            {snippet.final_bbox_source && (
              <Badge variant="secondary" className="text-[10px]">
                Final: {snippet.final_bbox_source}
              </Badge>
            )}
            {/* CR575: Neutral bbox_hint indicator (informational only) */}
            {snippet.bbox_hint_present != null && (
              <Badge variant="outline" className="text-[10px]">
                {snippet.bbox_hint_present ? 'Hint: available from S1.3' : 'Hint: none (S1.3)'}
              </Badge>
            )}
          </SheetTitle>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-1">
            {snippet.confidence_final != null ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help">
                      Confidence: <strong className={snippet.confidence_final < 0.5 ? "text-destructive" : snippet.confidence_final < 0.7 ? "text-amber-600" : "text-primary"}>
                        {(snippet.confidence_final * 100).toFixed(0)}%
                      </strong>
                      {snippet.bbox_confidence != null && snippet.confidence_final !== snippet.bbox_confidence && (
                        <span className="text-muted-foreground ml-1">(raw: {(snippet.bbox_confidence * 100).toFixed(0)}%)</span>
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs space-y-1 max-w-xs">
                    <p><strong>Calibrated confidence</strong> = raw × clamp(evidence, 0.25..1.0)</p>
                    <p>Raw model: {snippet.bbox_confidence != null ? `${(snippet.bbox_confidence * 100).toFixed(0)}%` : "—"}</p>
                    <p>Evidence score: {snippet.evidence_score != null ? `${(snippet.evidence_score * 100).toFixed(0)}%` : "—"}</p>
                    {snippet.evidence_hits && (
                      <p>Hits: brand={snippet.evidence_hits.brand ?? 0} value={snippet.evidence_hits.value ?? 0} mechanic={snippet.evidence_hits.mechanic ?? 0} (of {snippet.evidence_hits.tokens_checked ?? "?"})</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : snippet.bbox_confidence != null ? (
              <span>Confidence: <strong>{(snippet.bbox_confidence * 100).toFixed(0)}%</strong></span>
            ) : null}
            {snippet.evidence_score != null && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant={snippet.evidence_score >= 0.6 ? "default" : snippet.evidence_score >= 0.3 ? "secondary" : "destructive"}
                      className="text-[10px] cursor-help"
                    >
                      Evidence {(snippet.evidence_score * 100).toFixed(0)}%
                      {snippet.evidence_hits ? ` · ${snippet.evidence_hits.total ?? 0} hits` : ""}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs space-y-1 max-w-xs">
                    <p><strong>Brand hits:</strong> {snippet.evidence_hits?.brand ?? 0}</p>
                    <p><strong>Value hits:</strong> {snippet.evidence_hits?.value ?? 0}</p>
                    <p><strong>Mechanic hits:</strong> {snippet.evidence_hits?.mechanic ?? 0}</p>
                    <p><strong>Tokens checked:</strong> {snippet.evidence_hits?.tokens_checked ?? 0}</p>
                    {snippet.evidence_pass_a && (
                      <p><strong>Pass A evidence:</strong> {(snippet.evidence_pass_a.score ?? 0 * 100).toFixed(0)}% ({snippet.evidence_pass_a.hits ?? 0} hits)</p>
                    )}
                    {snippet.matched_evidence_tokens && snippet.matched_evidence_tokens.length > 0 && (
                      <p><strong>Matched:</strong> {snippet.matched_evidence_tokens.join(", ")}</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {snippet.bbox_score != null && (
              <span>Score: <strong>{snippet.bbox_score.toFixed(2)}</strong></span>
            )}
            {snippet.bbox_locator_pass && (
              <span>Strategy: <strong>{snippet.bbox_locator_pass}</strong></span>
            )}
            {snippet.cropper_used && (
              <span className="text-primary font-medium">✓ Cropper refined</span>
            )}
            {delta && deltaClass && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant={deltaClass === "good" ? "default" : deltaClass === "bad" ? "destructive" : "secondary"}
                      className="text-[10px] cursor-help"
                    >
                      Δ {deltaClass === "good" ? "Good" : deltaClass === "bad" ? "Bad" : "Neutral"} · IoU {delta.iou.toFixed(2)} · area×{delta.areaMult.toFixed(2)} · shift {delta.shift.toFixed(2)}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs space-y-1 max-w-xs">
                    <p><strong>Pass A area:</strong> {passABbox ? (bboxArea(passABbox) * 100).toFixed(1) + "%" : "—"}</p>
                    <p><strong>Pass B area:</strong> {passBBbox ? (bboxArea(passBBbox) * 100).toFixed(1) + "%" : "—"}</p>
                    <p><strong>Final source:</strong> {snippet.final_bbox_source ?? "unknown"}</p>
                    <p><strong>Cropper used:</strong> {snippet.cropper_used ? "yes" : "no"}</p>
                    <p><strong>IoU:</strong> {delta.iou.toFixed(3)} — <strong>Area mult:</strong> {delta.areaMult.toFixed(3)} — <strong>Shift:</strong> {delta.shift.toFixed(3)}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex gap-1 mt-1">
            {snippet.staging_item_id && (
              <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 px-2"
                onClick={() => copyText(snippet.staging_item_id!, "Staging item ID")}>
                <Copy className="h-3 w-3" /> staging_item
              </Button>
            )}
            {snippet.deal_id && (
              <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 px-2"
                onClick={() => copyText(snippet.deal_id!, "Deal ID")}>
                <Copy className="h-3 w-3" /> deal_id
              </Button>
            )}
          </div>
          {/* CR585: Truth override badge + lock button */}
          <div className="flex items-center gap-2 mt-1.5">
            {(snippet.truth_override_source || truthLocked) && (
              <Badge variant="outline" className="text-[10px] border-emerald-500/50 text-emerald-700 dark:text-emerald-400">
                🔒 Truth bbox override: ON (source: {snippet.truth_override_source ?? 'admin_manual'})
              </Badge>
            )}
            {snippet.batch_run_id && snippet.staging_item_id && (snippet.locator_proposed_bbox_norm || snippet.bbox_norm) && !truthLocked && !snippet.truth_override_source && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] gap-1 px-2 border-emerald-500/30 text-emerald-700 hover:bg-emerald-500/10"
                disabled={lockingTruth}
                onClick={handleLockTruth}
              >
                🔒 {lockingTruth ? 'Locking...' : 'Lock proposal as truth'}
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="p-4">
          {urlLoading ? (
            <div className="space-y-3">
              <Skeleton className="w-full h-[300px] rounded-md" />
              <Skeleton className="w-1/2 h-4" />
            </div>
          ) : urlError ? (
            <div className="p-4 border border-destructive/30 bg-destructive/5 rounded-md text-sm text-destructive">
              {urlError}
            </div>
          ) : (
            <Tabs defaultValue="source" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="source" className="flex-1 gap-1 text-xs">
                  <ImageIcon className="h-3 w-3" /> Source Page
                </TabsTrigger>
                <TabsTrigger value="cropped" className="flex-1 gap-1 text-xs">
                  <Crop className="h-3 w-3" /> Cropped
                </TabsTrigger>
              </TabsList>

              <TabsContent value="source" className="mt-3">
                {sourceUrl ? (
                  <div>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <span className="text-xs text-muted-foreground">
                        Source page {bbox ? "with bbox overlay" : "(no bbox)"}
                      </span>
                      <div className="flex gap-1 flex-wrap">
                        {hasMultiplePasses && (
                          <>
                            <Button
                              variant={showPassA ? "default" : "outline"}
                              size="sm" className="h-7 text-xs gap-1"
                              onClick={() => setShowPassA(!showPassA)}
                            >
                              <Target className="h-3 w-3" /> Pass A
                            </Button>
                            {passBBbox && (
                              <Button
                                variant={showPassB ? "default" : "outline"}
                                size="sm" className="h-7 text-xs gap-1"
                                onClick={() => setShowPassB(!showPassB)}
                              >
                                <Crop className="h-3 w-3" /> Pass B
                              </Button>
                            )}
                            <Button
                              variant={showFinal ? "default" : "outline"}
                              size="sm" className="h-7 text-xs gap-1"
                              onClick={() => setShowFinal(!showFinal)}
                            >
                              <Layers className="h-3 w-3" /> Final
                            </Button>
                          </>
                         )}
                        {bbox && (
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleZoom}>
                            <ZoomIn className="h-3 w-3" /> Zoom
                          </Button>
                        )}
                        {/* CR577: Proposal ghost toggle */}
                        {proposedBbox && proposedDiffersFromFinal && (
                          <Button
                            variant={showProposal ? "default" : "outline"}
                            size="sm" className="h-7 text-xs gap-1"
                            onClick={() => setShowProposal(!showProposal)}
                          >
                            <Target className="h-3 w-3" /> Proposal
                          </Button>
                        )}
                        {zoomed && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setZoomed(false)}>
                            <X className="h-3 w-3" /> Reset
                          </Button>
                        )}
                      </div>
                    </div>
                    <div
                      className="relative border rounded-md overflow-auto bg-muted/30"
                      style={{ maxHeight: zoomed ? "none" : 500 }}
                    >
                      <div className="relative inline-block" style={{ transform: zoomed ? "scale(2)" : "none", transformOrigin: "top left" }}>
                        <img
                          src={sourceUrl}
                          alt={`Source page ${snippet.page_number}`}
                          className="block max-w-full"
                          draggable={false}
                        />
                        {/* Pass A bbox (dashed amber) */}
                        {showPassA && passABbox && (
                          <div
                            className="absolute border-2 border-dashed border-amber-500/80 bg-amber-500/5 pointer-events-none"
                            style={{
                              left: `${passABbox.x * 100}%`,
                              top: `${passABbox.y * 100}%`,
                              width: `${passABbox.width * 100}%`,
                              height: `${passABbox.height * 100}%`,
                            }}
                          >
                            <span className="absolute -top-4 left-0 text-[10px] bg-amber-500 text-white px-1 rounded">A</span>
                          </div>
                        )}
                        {/* Pass B bbox (dashed green) */}
                        {showPassB && passBBbox && (
                          <div
                            className="absolute border-2 border-dashed border-green-500/80 bg-green-500/5 pointer-events-none"
                            style={{
                              left: `${passBBbox.x * 100}%`,
                              top: `${passBBbox.y * 100}%`,
                              width: `${passBBbox.width * 100}%`,
                              height: `${passBBbox.height * 100}%`,
                            }}
                          >
                            <span className="absolute -top-4 left-0 text-[10px] bg-green-500 text-white px-1 rounded">B</span>
                          </div>
                        )}
                        {/* Final bbox (solid primary) */}
                        {showFinal && bbox && (
                          <div
                            ref={bboxRef}
                            className="absolute border-2 border-primary/80 bg-primary/10 pointer-events-none"
                            style={{
                              left: `${bbox.x * 100}%`,
                              top: `${bbox.y * 100}%`,
                              width: `${bbox.width * 100}%`,
                              height: `${bbox.height * 100}%`,
                            }}
                          />
                        )}
                        {/* CR577: Ghost bbox — locator proposal (dashed blue) */}
                        {showProposal && proposedBbox && proposedDiffersFromFinal && (
                          <div
                            className="absolute border-2 border-dashed border-sky-400/70 bg-sky-400/5 pointer-events-none"
                            style={{
                              left: `${proposedBbox.x * 100}%`,
                              top: `${proposedBbox.y * 100}%`,
                              width: `${proposedBbox.width * 100}%`,
                              height: `${proposedBbox.height * 100}%`,
                            }}
                          >
                            <span className="absolute -top-4 left-0 text-[10px] bg-sky-500 text-white px-1 rounded">Proposal</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CR577: Overlay legend */}
                    {(bbox || (proposedBbox && proposedDiffersFromFinal)) && (
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                        <span>Overlay:</span>
                        {bbox && <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-primary/80" /> final (solid)</span>}
                        {proposedBbox && proposedDiffersFromFinal && (
                          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 border-t-2 border-dashed border-sky-400/70" /> proposal (dashed)</span>
                        )}
                        {!bbox && proposedBbox && proposedDiffersFromFinal && (
                          <span className="italic">proposal only — no accepted bbox</span>
                        )}
                      </div>
                    )}

                    {/* CR558: Pass A Strategy Overview — primary vs fallback debug */}
                    {snippet.pass_a_debug && (
                      <div className="mt-3 p-3 border rounded-md bg-muted/30 space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium">
                          <Layers className="h-3 w-3" />
                          Pass A Strategy
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">Final source:</span>
                          <Badge variant={snippet.pass_a_debug.finalSource === 'primary' ? 'default' : 'warning'}
                            className="text-[10px] px-1.5 py-0">
                            {snippet.pass_a_debug.finalSource === 'primary' ? 'Primary (prompt pack)' : 'Fallback'}
                          </Badge>
                          {snippet.pass_a_debug.fallbackTrigger && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {snippet.pass_a_debug.fallbackTrigger}
                            </Badge>
                          )}
                        </div>
                        <div className={`grid gap-3 text-xs ${snippet.pass_a_debug.fallback ? 'grid-cols-2' : 'grid-cols-1'}`}>
                          {/* Primary column */}
                          <div className="space-y-1 p-2 rounded border bg-background">
                            <div className="font-medium text-[10px] text-muted-foreground uppercase tracking-wider">Primary (prompt pack)</div>
                            <div className="text-[10px]">
                              <span className="text-muted-foreground">Decision:</span>{" "}
                              <span className={`font-medium ${
                                snippet.pass_a_debug.primary?.decision === 'localized' ? 'text-primary' : 'text-destructive'
                              }`}>{snippet.pass_a_debug.primary?.decision ?? '—'}</span>
                            </div>
                            {snippet.pass_a_debug.primary?.confidence != null && (
                              <div className="text-[10px]">
                                <span className="text-muted-foreground">Confidence:</span>{" "}
                                <strong>{(snippet.pass_a_debug.primary.confidence * 100).toFixed(0)}%</strong>
                              </div>
                            )}
                            {snippet.pass_a_debug.primary?.anchorHits != null && (
                              <div className="text-[10px]">
                                <span className="text-muted-foreground">Anchor hits:</span>{" "}
                                <strong>{snippet.pass_a_debug.primary.anchorHits}</strong>
                              </div>
                            )}
                            <div className="text-[10px]">
                              <span className="text-muted-foreground">Must-hit ok:</span>{" "}
                              {snippet.pass_a_debug.primary?.mustHitSatisfied ? '✅' : '❌'}
                            </div>
                            {snippet.pass_a_debug.primary?.evidenceScore != null && (
                              <div className="text-[10px]">
                                <span className="text-muted-foreground">Evidence:</span>{" "}
                                {(snippet.pass_a_debug.primary.evidenceScore * 100).toFixed(0)}%
                              </div>
                            )}
                            {snippet.pass_a_debug.primary?.reason && (
                              <div className="text-[10px] text-muted-foreground font-mono mt-1 max-h-16 overflow-auto">
                                {snippet.pass_a_debug.primary.reason}
                              </div>
                            )}
                          </div>
                          {/* Fallback column */}
                          {snippet.pass_a_debug.fallback && (
                            <div className="space-y-1 p-2 rounded border bg-background">
                              <div className="font-medium text-[10px] text-muted-foreground uppercase tracking-wider">Fallback ({snippet.pass_a_debug.fallback.strategy})</div>
                              {snippet.pass_a_fallback_quadrant && (
                                <div className="text-[10px]">
                                  <span className="text-muted-foreground">Quadrant:</span>{" "}
                                  <strong>{snippet.pass_a_fallback_quadrant}</strong>
                                </div>
                              )}
                              {snippet.pass_a_debug.fallback.confidence != null && (
                                <div className="text-[10px]">
                                  <span className="text-muted-foreground">Confidence:</span>{" "}
                                  <strong>{(snippet.pass_a_debug.fallback.confidence * 100).toFixed(0)}%</strong>
                                </div>
                              )}
                              {snippet.pass_a_debug.fallback.reason && (
                                <div className="text-[10px] text-muted-foreground font-mono mt-1 max-h-16 overflow-auto">
                                  {snippet.pass_a_debug.fallback.reason}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {/* CR558: Anchor gate reason */}
                        {snippet.pass_a_anchor_gate && (
                          <div className={`mt-1 p-2 rounded text-[10px] space-y-0.5 ${
                            snippet.pass_a_anchor_gate.gate_status === 'pass' ? 'bg-primary/5 border border-primary/20'
                            : snippet.pass_a_anchor_gate.gate_status === 'fail' ? 'bg-destructive/5 border border-destructive/20'
                            : 'bg-muted/50 border'
                          }`}>
                            <div className="font-medium">
                              Anchor Gate:{" "}
                              <Badge variant={snippet.pass_a_anchor_gate.gate_status === 'pass' ? 'default' : snippet.pass_a_anchor_gate.gate_status === 'fail' ? 'destructive' : 'secondary'}
                                className="text-[10px] px-1.5 py-0">
                                {snippet.pass_a_anchor_gate.gate_status}
                              </Badge>
                              {snippet.pass_a_anchor_gate.fail_code && (
                                <span className="text-destructive ml-1">{snippet.pass_a_anchor_gate.fail_code}</span>
                              )}
                            </div>
                            {snippet.pass_a_anchor_gate.missing_must_hit_tokens && snippet.pass_a_anchor_gate.missing_must_hit_tokens.length > 0 && (
                              <div className="text-muted-foreground">
                                Missing must-hit: <span className="text-destructive font-mono">{snippet.pass_a_anchor_gate.missing_must_hit_tokens.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {/* CR558: Primary vs Fallback bbox coords comparison */}
                        {snippet.pass_a_primary_bbox_norm && snippet.pass_a_fallback_bbox_norm && (
                          <div className="text-[10px] font-mono text-muted-foreground space-y-0.5 mt-1">
                            <div>Primary bbox: x:{snippet.pass_a_primary_bbox_norm.x.toFixed(3)} y:{snippet.pass_a_primary_bbox_norm.y.toFixed(3)} w:{snippet.pass_a_primary_bbox_norm.width.toFixed(3)} h:{snippet.pass_a_primary_bbox_norm.height.toFixed(3)}</div>
                            <div>Fallback bbox: x:{snippet.pass_a_fallback_bbox_norm.x.toFixed(3)} y:{snippet.pass_a_fallback_bbox_norm.y.toFixed(3)} w:{snippet.pass_a_fallback_bbox_norm.width.toFixed(3)} h:{snippet.pass_a_fallback_bbox_norm.height.toFixed(3)}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CR520: Pass A Diagnostics */}
                    {snippet.pass_a_bbox_norm && (
                      <div className="mt-3 p-3 border rounded-md bg-amber-500/5 border-amber-500/20 space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium">
                          <Target className="h-3 w-3 text-amber-600" />
                          Cropper Pass A Diagnostics
                          <span className="text-[10px] text-muted-foreground font-normal">First-pass locator / primary prompt pack</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Attempted:</span>{" "}
                            <span className="font-medium">Yes</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Used:</span>{" "}
                            <span className={`font-medium ${(snippet.final_bbox_source === 'locator' || snippet.final_bbox_source === 'pass_a') ? 'text-primary' : 'text-muted-foreground'}`}>
                              {(snippet.final_bbox_source === 'locator' || snippet.final_bbox_source === 'pass_a') ? 'Yes ✓' : 'No'}
                            </span>
                          </div>
                          {/* CR574: Image sent indicator */}
                          <div>
                            <span className="text-muted-foreground">Image sent:</span>{" "}
                            <span className={`font-medium ${snippet.ai_payload_has_image ? 'text-primary' : 'text-destructive'}`}>
                              {snippet.ai_payload_has_image ? 'Yes ✓' : 'No'}
                            </span>
                          </div>
                          {snippet.bbox_confidence != null && (
                            <div>
                              <span className="text-muted-foreground">Confidence:</span>{" "}
                              <strong>{(snippet.bbox_confidence * 100).toFixed(0)}%</strong>
                              {snippet.min_locator_confidence_used != null && (
                                <span className="text-muted-foreground ml-1">
                                  (threshold: {(snippet.min_locator_confidence_used * 100).toFixed(0)}%)
                                </span>
                              )}
                            </div>
                          )}
                          {/* CR574: Box must/should hit counts */}
                          {snippet.box_must_hit_count != null && (
                            <div>
                              <span className="text-muted-foreground">Box hits:</span>{" "}
                              <strong>{snippet.box_must_hit_count}</strong> must
                              {snippet.box_should_hit_count != null && (
                                <span> / <strong>{snippet.box_should_hit_count}</strong> should</span>
                              )}
                            </div>
                          )}
                          {/* CR575: Anchor gate result with full detail */}
                          {snippet.anchor_gate_result && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Anchor gate:</span>{" "}
                              <Badge variant={
                                snippet.anchor_gate_result === 'passed' ? 'default' 
                                : snippet.anchor_gate_result === 'rejected' ? 'destructive' 
                                : snippet.anchor_gate_result === 'ocr_mismatch' ? 'secondary'
                                : 'secondary'
                              } className={`text-[10px] px-1.5 py-0 ${snippet.anchor_gate_result === 'ocr_mismatch' ? 'bg-amber-500 text-white' : ''}`}>
                                {snippet.anchor_gate_result}
                              </Badge>
                              {snippet.anchor_gate_reject_reason && (
                                <span className="text-[10px] text-destructive ml-1">{snippet.anchor_gate_reject_reason}</span>
                              )}
                              {snippet.anchor_gate_must_hit_count != null && snippet.anchor_gate_must_hit_required != null && (
                                <span className="text-[10px] text-muted-foreground ml-2">
                                  (must: {snippet.anchor_gate_must_hit_count}/{snippet.anchor_gate_must_hit_required})
                                </span>
                              )}
                            </div>
                          )}
                          {snippet.bbox_score != null && (
                            <div>
                              <span className="text-muted-foreground">Score:</span>{" "}
                              <strong>{Number(snippet.bbox_score).toFixed(3)}</strong>
                            </div>
                          )}
                          {snippet.evidence_pass_a && (
                            <div>
                              <span className="text-muted-foreground">Evidence:</span>{" "}
                              <span>{((snippet.evidence_pass_a.score ?? 0) * 100).toFixed(0)}% ({snippet.evidence_pass_a.hits ?? 0} hits)</span>
                            </div>
                          )}
                          {snippet.render_source && (
                            <div>
                              <span className="text-muted-foreground">Strategy:</span>{" "}
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{snippet.render_source}</Badge>
                            </div>
                          )}
                          {snippet.fallback_source && (
                            <div>
                              <span className="text-muted-foreground">Fallback:</span>{" "}
                              <Badge variant={snippet.fallback_source === 'last_resort' || snippet.fallback_source === 'quadrant_v1' ? 'destructive' : 'secondary'} className="text-[10px] px-1.5 py-0">
                                {snippet.fallback_source}
                              </Badge>
                              {snippet.fallback_strategy && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 ml-1 text-amber-600 border-amber-500/30">
                                  strategy: {snippet.fallback_strategy}
                                </Badge>
                              )}
                            </div>
                          )}
                          {snippet.needs_review && (
                            <div>
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                                ⚠ Needs review
                              </Badge>
                              {snippet.cr614_original_status && (
                                <span className="text-muted-foreground text-[10px] ml-1">
                                  (was: {snippet.cr614_original_status})
                                </span>
                              )}
                            </div>
                          )}
                          {snippet.fallback_policy && (
                            <div>
                              <span className="text-muted-foreground">Fallback policy:</span>{" "}
                              <Badge variant={snippet.fallback_policy === 'strict' ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                                {snippet.fallback_policy}
                              </Badge>
                            </div>
                          )}
                          {snippet.bbox_locator_pass && (
                            <div>
                              <span className="text-muted-foreground">Locator pass:</span>{" "}
                              <span className="font-mono text-[10px]">{snippet.bbox_locator_pass}</span>
                            </div>
                          )}
                        </div>
                        {snippet.locator_reason && (
                          <div>
                            <span className="text-xs text-muted-foreground">Reason:</span>{" "}
                            <span className="text-xs">{snippet.locator_reason}</span>
                          </div>
                        )}
                        {/* Show Pass A bbox coords */}
                        <div>
                          <span className="text-xs text-muted-foreground">Bbox (norm):</span>{" "}
                          <span className="text-[10px] font-mono">
                            x:{snippet.pass_a_bbox_norm.x.toFixed(3)} y:{snippet.pass_a_bbox_norm.y.toFixed(3)} w:{snippet.pass_a_bbox_norm.width.toFixed(3)} h:{snippet.pass_a_bbox_norm.height.toFixed(3)}
                          </span>
                        </div>
                        {/* CR556: Trim diagnostics */}
                        {snippet.trim_applied && (
                          <div className="p-1.5 rounded border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 space-y-0.5">
                            <Badge className="text-[10px] px-1.5 py-0 bg-emerald-500 text-white">
                              ✂ Trimmed — {snippet.trim_reason ?? "anchor_cluster"}
                            </Badge>
                            {snippet.trim_bbox_norm && (
                              <div className="text-[10px] font-mono text-muted-foreground">
                                Trimmed bbox: x:{(snippet.trim_bbox_norm as any).x?.toFixed(3)} y:{(snippet.trim_bbox_norm as any).y?.toFixed(3)} w:{(snippet.trim_bbox_norm as any).width?.toFixed(3)} h:{(snippet.trim_bbox_norm as any).height?.toFixed(3)}
                              </div>
                            )}
                            {snippet.trim_removed_tokens_sample && Array.isArray(snippet.trim_removed_tokens_sample) && snippet.trim_removed_tokens_sample.length > 0 && (
                              <div className="text-[10px] text-muted-foreground">
                                Removed: {(snippet.trim_removed_tokens_sample as string[]).join(", ")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* CR557: Panel expansion diagnostics */}
                    {snippet.pass_a_expansion_applied && (
                      <div className="mt-3 p-2 border rounded-md bg-blue-500/5 border-blue-500/20 space-y-1">
                        <div className="flex items-center gap-2 text-xs font-medium">
                          <ArrowDown className="h-3 w-3 text-blue-600" />
                          Panel Expansion (CR557)
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {snippet.pass_a_expansion_mode === "conditions_sniffer" ? "📋 Conditions found" : "📏 Bottom pad"}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-[10px] text-muted-foreground">
                          <div>Height: {((snippet.pass_a_panel_height_initial ?? 0) * 100).toFixed(1)}% → {((snippet.pass_a_panel_height_final ?? 0) * 100).toFixed(1)}%</div>
                          <div>Conditions: {snippet.pass_a_conditions_detected ? "✅ detected" : "—"}</div>
                        </div>
                        {snippet.pass_a_conditions_band_text_200 && (
                          <div className="text-[10px] font-mono text-muted-foreground bg-muted/40 p-1 rounded max-h-12 overflow-auto">
                            {snippet.pass_a_conditions_band_text_200}
                          </div>
                        )}
                      </div>
                    )}

                    {/* CR575: Locator Proposal panel — always shown when proposed metrics exist */}
                    {(snippet.locator_proposed_confidence != null || snippet.locator_proposed_area_ratio != null) && (
                      <div className="mt-3 p-3 border rounded-md bg-muted/30 space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium">
                          <Target className="h-3 w-3" />
                          Locator Proposal
                          <span className="text-[10px] text-muted-foreground font-normal">
                            What the model proposed{snippet.anchor_gate_result === 'rejected' ? ' (rejected by anchor gate)' : ''}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {snippet.locator_proposed_confidence != null && (
                            <div>
                              <span className="text-muted-foreground">Proposed confidence:</span>{" "}
                              <strong>{(snippet.locator_proposed_confidence * 100).toFixed(0)}%</strong>
                            </div>
                          )}
                          {snippet.locator_proposed_area_ratio != null && (
                            <div>
                              <span className="text-muted-foreground">Proposed area:</span>{" "}
                              <strong>{(snippet.locator_proposed_area_ratio * 100).toFixed(1)}%</strong>
                              {snippet.locator_proposed_quality_hint && (
                                <span className="ml-1">
                                  (<span className={
                                    snippet.locator_proposed_quality_hint === 'tight' ? 'text-green-600' :
                                    snippet.locator_proposed_quality_hint === 'medium' ? 'text-primary' :
                                    snippet.locator_proposed_quality_hint === 'bad' ? 'text-destructive' : 'text-muted-foreground'
                                  }>{snippet.locator_proposed_quality_hint}</span>)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {/* CR577: Proposal Box OCR diagnostics — always shown when data exists */}
                        {(snippet.proposal_box_must_hit_count != null || snippet.anchor_gate_result) && (
                          <div className={`mt-1 p-2 rounded text-xs space-y-1 ${
                            snippet.anchor_gate_result === 'rejected'
                              ? 'bg-destructive/5 border border-destructive/20'
                              : snippet.anchor_gate_result === 'ocr_mismatch'
                                ? 'bg-amber-500/5 border border-amber-500/20'
                                : snippet.anchor_gate_result === 'passed_soft'
                                  ? 'bg-teal-500/5 border border-teal-500/20'
                                  : snippet.anchor_gate_result === 'passed_soft_fallback'
                                    ? 'bg-cyan-500/5 border border-cyan-500/20'
                                    : 'bg-green-500/5 border border-green-500/20'
                          }`}>
                            <p className="font-medium">
                              {snippet.anchor_gate_result === 'rejected'
                                ? <span className="text-destructive">Gate: Failed</span>
                                : snippet.anchor_gate_result === 'ocr_mismatch'
                                  ? <span className="text-amber-600">Gate: Skipped — OCR Page Mismatch</span>
                                  : snippet.anchor_gate_result === 'passed_soft'
                                    ? <span className="text-teal-600">Gate: Passed (soft — tight box)</span>
                                    : snippet.anchor_gate_result === 'passed_soft_fallback'
                                      ? <span className="text-cyan-600">Gate: Passed (soft fallback — proposal used despite partial anchors)</span>
                                      : <span className="text-green-700">Gate: Passed</span>}
                              {snippet.anchor_gate_reject_reason && (
                                <span className="text-destructive ml-1 font-normal">— {snippet.anchor_gate_reject_reason}</span>
                              )}
                            </p>
                            {/* CR580: OCR page mismatch details */}
                            {snippet.anchor_gate_result === 'ocr_mismatch' && snippet.ocr_page_merchant_hint && (
                              <div className="p-1.5 rounded bg-amber-500/10 border border-amber-500/20">
                                <p className="text-amber-700 dark:text-amber-400">
                                  ⚠️ OCR text appears to be from a different merchant/page
                                </p>
                                <p className="text-muted-foreground">
                                  Deal merchant: <strong>{snippet.ocr_page_merchant_hint.deal_merchant_source_id ?? 'unknown'}</strong>
                                </p>
                                <p className="text-muted-foreground">
                                  OCR source: <strong>{snippet.ocr_page_merchant_hint.ocr_source_merchant_id ?? 'unknown'}</strong>
                                </p>
                              </div>
                            )}
                            {/* CR578: Core/support breakdown */}
                            {snippet.proposal_core_hits != null && (
                              <p>
                                Core hits: <strong>{snippet.proposal_core_hits}</strong> / {snippet.proposal_core_total ?? '?'}
                                <span className="text-muted-foreground ml-2">
                                  Support: <strong>{snippet.proposal_support_hits ?? 0}</strong>
                                </span>
                                <span className="text-muted-foreground ml-2">
                                  Total: <strong>{(snippet.proposal_box_must_hit_count ?? 0) + (snippet.proposal_box_should_hit_count ?? 0)}</strong> / {snippet.anchor_gate_threshold_min_total ?? 3}
                                </span>
                                {snippet.anchor_gate_rule_version && (
                                  <Badge variant="outline" className="text-[9px] px-1 py-0 ml-2">{snippet.anchor_gate_rule_version}</Badge>
                                )}
                                {snippet.anchor_token_strategy_version && snippet.anchor_token_strategy_version !== 'default_v1' && (
                                  <Badge variant="secondary" className="text-[9px] px-1 py-0 ml-1">🎯 {snippet.anchor_token_strategy_version}</Badge>
                                )}
                              </p>
                            )}
                            {snippet.proposal_box_must_hit_count != null && !snippet.proposal_core_hits && (
                              <p>
                                Must-hit in box: <strong>{snippet.proposal_box_must_hit_count}</strong> / {snippet.proposal_box_must_hit_total ?? '?'}
                              </p>
                            )}
                            {snippet.proposal_box_anchor_hits && snippet.proposal_box_anchor_hits.length > 0 && (
                              <div>
                                <span className="text-muted-foreground">Anchor hits:</span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {snippet.proposal_box_anchor_hits.map((t, i) => (
                                    <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 border-green-500/50">{t}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {snippet.proposal_box_missing_must && snippet.proposal_box_missing_must.length > 0 && (
                              <div>
                                <span className="text-muted-foreground">Missing must:</span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {snippet.proposal_box_missing_must.map((t, i) => (
                                    <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 border-destructive/50 text-destructive">{t}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* CR581: "OCR inside proposal box" subsection */}
                            <div className="mt-2 p-2 rounded bg-muted/30 border border-muted space-y-1.5">
                              <div className="text-[11px] font-medium flex items-center gap-1">
                                🔍 OCR inside proposal box
                                {snippet.box_ocr_engine && snippet.box_ocr_engine !== 'none' && (
                                  <Badge variant="outline" className="text-[9px] px-1 py-0 ml-1">{snippet.box_ocr_engine}</Badge>
                                )}
                                {snippet.ocr_box_sanity_status && (
                                  <Badge
                                    variant={snippet.ocr_box_sanity_status === 'suspicious' ? 'destructive' : 'secondary'}
                                    className="text-[9px] px-1 py-0"
                                  >
                                    {snippet.ocr_box_sanity_status}
                                  </Badge>
                                )}
                              </div>
                              {/* CR554: Fall back to box_ocr_text_200 when proposal_box_ocr_text_200 is null */}
                              {(snippet.proposal_box_ocr_text_200 || (snippet as any).box_ocr_text_200) ? (
                                <pre className="text-[10px] font-mono bg-background/80 p-2 rounded max-h-24 overflow-auto whitespace-pre-wrap border">
                                  {snippet.proposal_box_ocr_text_200 || (snippet as any).box_ocr_text_200}
                                </pre>
                              ) : (
                                <p className="text-[10px] text-muted-foreground italic">No bbox OCR text available</p>
                              )}
                              {/* Anchor hits chips */}
                              {snippet.proposal_box_anchor_hits && snippet.proposal_box_anchor_hits.length > 0 && (
                                <div>
                                  <span className="text-[10px] text-muted-foreground">✅ Hits:</span>
                                  <div className="flex flex-wrap gap-0.5 mt-0.5">
                                    {snippet.proposal_box_anchor_hits.map((t, i) => (
                                      <Badge key={i} variant="outline" className="text-[9px] px-1 py-0 border-green-500/50 text-green-700 dark:text-green-400">{t}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Missing must chips */}
                              {snippet.proposal_box_missing_must && snippet.proposal_box_missing_must.length > 0 && (
                                <div>
                                  <span className="text-[10px] text-muted-foreground">❌ Missing must:</span>
                                  <div className="flex flex-wrap gap-0.5 mt-0.5">
                                    {snippet.proposal_box_missing_must.map((t, i) => (
                                      <Badge key={i} variant="outline" className="text-[9px] px-1 py-0 border-destructive/50 text-destructive">{t}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Missing should chips */}
                              {snippet.proposal_box_missing_should && snippet.proposal_box_missing_should.length > 0 && (
                                <div>
                                  <span className="text-[10px] text-muted-foreground">⚠ Missing should:</span>
                                  <div className="flex flex-wrap gap-0.5 mt-0.5">
                                    {snippet.proposal_box_missing_should.map((t, i) => (
                                      <Badge key={i} variant="outline" className="text-[9px] px-1 py-0 border-amber-500/50 text-amber-600 dark:text-amber-400">{t}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* CR581: OCR sanity indicator */}
                            {snippet.ocr_sanity_status && (
                              <div className="text-xs text-muted-foreground mt-1">
                                OCR sanity: <strong className={snippet.ocr_sanity_status === 'flaky' ? 'text-warning' : ''}>{snippet.ocr_sanity_status}</strong>
                                {snippet.ocr_sanity_reason && (
                                  <> — <span className="font-mono text-[10px]">{snippet.ocr_sanity_reason}</span></>
                                )}
                              </div>
                            )}
                            {/* CR580/CR582: Page OCR source indicator */}
                            {snippet.ocr_page_source && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Page OCR source:{' '}
                                <strong className={
                                  snippet.ocr_page_source.startsWith('truth:') ? 'text-green-600'
                                    : snippet.ocr_page_source === 's1_4_fresh' ? 'text-sky-600'
                                    : snippet.ocr_page_source === 'deal.ocr_text' ? 'text-amber-600'
                                    : ''
                                }>
                                  {snippet.ocr_page_source.startsWith('truth:')
                                    ? `S1.2 canonical (${snippet.ocr_page_source})`
                                    : snippet.ocr_page_source === 's1_4_fresh'
                                      ? 'fresh (S1.4 Vision)'
                                      : snippet.ocr_page_source === 'deal.ocr_text'
                                        ? 'deal.ocr_text (legacy — may be wrong page)'
                                        : snippet.ocr_page_source}
                                </strong>
                                {snippet.ocr_page_text_len != null && (
                                  <> — {snippet.ocr_page_text_len} chars</>
                                )}
                              </div>
                            )}
                            {/* CR583: Box OCR sanity indicator */}
                            {snippet.ocr_box_sanity_status && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Box sanity: <strong className={snippet.ocr_box_sanity_status === 'suspicious' ? 'text-amber-600' : ''}>{snippet.ocr_box_sanity_status}</strong>
                                {snippet.ocr_box_sanity_reason && (
                                  <> — <span className="font-mono text-[10px]">{snippet.ocr_box_sanity_reason}</span></>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* CR579: Box Comparison — side-by-side proposal vs final (debug view only) */}
                    {debugView && (snippet.locator_proposed_confidence != null || proposedBbox) && (
                      <div className="mt-3 p-3 border rounded-md bg-muted/20 border-muted space-y-2">
                        <div className="text-xs font-medium">📊 Box Comparison — Proposal vs Final</div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          {/* Proposal column */}
                          <div className="space-y-1 p-2 rounded bg-sky-500/5 border border-sky-500/20">
                            <div className="font-medium text-sky-700 dark:text-sky-400">Proposal (model)</div>
                            {proposedBbox && (
                              <div className="font-mono text-[10px]">
                                x:{proposedBbox.x.toFixed(3)} y:{proposedBbox.y.toFixed(3)}<br/>
                                w:{proposedBbox.width.toFixed(3)} h:{proposedBbox.height.toFixed(3)}
                              </div>
                            )}
                            {snippet.locator_proposed_confidence != null && (
                              <div>Confidence: <strong>{(snippet.locator_proposed_confidence * 100).toFixed(0)}%</strong></div>
                            )}
                            <div>Gate: {snippet.anchor_gate_result === 'passed'
                              ? <span className="text-green-600 font-medium">passed</span>
                              : snippet.anchor_gate_result === 'rejected'
                                ? <span className="text-destructive font-medium">rejected</span>
                                : snippet.anchor_gate_result === 'ocr_mismatch'
                                  ? <span className="text-amber-600 font-medium">skipped (OCR mismatch)</span>
                                  : <span className="text-muted-foreground">—</span>}
                            </div>
                            {snippet.proposal_box_must_hit_count != null && (
                              <div>Must-hit: <strong>{snippet.proposal_box_must_hit_count}</strong>/{snippet.proposal_box_must_hit_total ?? '?'}</div>
                            )}
                            {snippet.proposal_box_should_hit_count != null && (
                              <div>Should-hit: <strong>{snippet.proposal_box_should_hit_count}</strong></div>
                            )}
                            {snippet.proposal_box_anchor_hits && snippet.proposal_box_anchor_hits.length > 0 && (
                              <div className="flex flex-wrap gap-0.5">
                                {snippet.proposal_box_anchor_hits.map((t, i) => (
                                  <Badge key={i} variant="outline" className="text-[9px] px-1 py-0">{t}</Badge>
                                ))}
                              </div>
                            )}
                            {snippet.proposal_box_missing_must && snippet.proposal_box_missing_must.length > 0 && (
                              <div>
                                <span className="text-muted-foreground">Missing:</span>
                                <div className="flex flex-wrap gap-0.5 mt-0.5">
                                  {snippet.proposal_box_missing_must.map((t, i) => (
                                    <Badge key={i} variant="outline" className="text-[9px] px-1 py-0 text-destructive border-destructive/40">{t}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Final column */}
                          <div className="space-y-1 p-2 rounded bg-primary/5 border border-primary/20">
                            <div className="font-medium text-primary">Final (used for crop)</div>
                            {bbox ? (
                              <>
                                <div className="font-mono text-[10px]">
                                  x:{bbox.x.toFixed(3)} y:{bbox.y.toFixed(3)}<br/>
                                  w:{bbox.width.toFixed(3)} h:{bbox.height.toFixed(3)}
                                </div>
                                <div>Source: <Badge variant="outline" className="text-[9px] px-1 py-0">{snippet.final_bbox_source ?? '?'}</Badge></div>
                                {(snippet as any).box_must_hit_count != null && (
                                  <div>Must-hit: <strong>{(snippet as any).box_must_hit_count}</strong></div>
                                )}
                                {(snippet as any).box_should_hit_count != null && (
                                  <div>Should-hit: <strong>{(snippet as any).box_should_hit_count}</strong></div>
                                )}
                                {(snippet as any).box_anchor_hits && (snippet as any).box_anchor_hits.length > 0 && (
                                  <div className="flex flex-wrap gap-0.5">
                                    {((snippet as any).box_anchor_hits as string[]).map((t, i) => (
                                      <Badge key={i} variant="outline" className="text-[9px] px-1 py-0">{t}</Badge>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-muted-foreground italic">No final bbox — not localizable</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}



                    {/* CR554: Root-level debug stamp */}
                    {snippet.debug_stamp && (
                      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                        CR554: {snippet.debug_stamp}
                      </div>
                    )}
                    {snippet.pass_b && (
                      <div className="mt-3 p-3 border rounded-md bg-muted/30 space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium flex-wrap">
                          <Target className="h-3 w-3" />
                          Cropper Pass B Diagnostics
                          <span className="text-[10px] text-muted-foreground font-normal">Refinement / second-pass crop</span>
                          {(snippet.snippet_status ?? "").toLowerCase() === "page_fallback" && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500 text-amber-700 dark:text-amber-300">
                              Recovered via page fallback
                            </Badge>
                          )}
                          {snippet.pass_b_jet_plane_guardrail_tripped && (
                            <Badge className="text-[10px] px-1.5 py-0 bg-destructive text-destructive-foreground">
                              🚨 Jet Plane Guardrail
                            </Badge>
                          )}
                        </div>
                        {/* CR554: Mic Check debug stamp */}
                        {snippet.pass_b?.debug_stamp && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            <span className="font-semibold">Debug:</span>{' '}
                            <span className="font-mono">{snippet.pass_b.debug_stamp}</span>
                          </div>
                        )}
                        {/* CR557: Pack info */}
                        {snippet.pass_b.pack?.prompt_pack_name && (
                          <div className="text-[10px] text-muted-foreground">
                            Pack: <span className="font-medium">{snippet.pass_b.pack.prompt_pack_name}</span>
                            {snippet.pass_b.pack.prompt_pack_version_label && (
                              <span className="ml-1">({snippet.pass_b.pack.prompt_pack_version_label})</span>
                            )}
                          </div>
                        )}
                        {/* CR557: Input provenance */}
                        {snippet.pass_b.input && (
                          <div className="text-[10px] text-muted-foreground space-y-0.5">
                            <div>
                              Input: <Badge variant="outline" className="text-[9px] px-1 py-0">{snippet.pass_b.input.source ?? "unknown"}</Badge>
                              {snippet.pass_b.input.area_ratio != null && (
                                <span className="ml-1">({(snippet.pass_b.input.area_ratio * 100).toFixed(1)}% of page)</span>
                              )}
                            </div>
                            {snippet.pass_b.input.px && (
                              <div>ROI: {snippet.pass_b.input.px.w}×{snippet.pass_b.input.px.h}px
                                {snippet.pass_b.input.page_px && (
                                  <span> · Page: {snippet.pass_b.input.page_px.w}×{snippet.pass_b.input.page_px.h}px</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {/* CR558: Decision + skip reason */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Decision:</span>{" "}
                            <Badge
                              variant={snippet.pass_b.decision === "refined" ? "default" : snippet.pass_b.decision === "internal_error" ? (((snippet.snippet_status ?? "").toLowerCase() === "page_fallback") ? "secondary" : "destructive") : snippet.pass_b.decision === "skipped" ? "secondary" : "outline"}
                              className="text-[10px] px-1 py-0"
                            >
                              {/* CR610: Show "skipped" not "failed" when AI wasn't invoked */}
                              {snippet.pass_b.decision ?? (snippet.pass_b.attempted
                                ? (snippet.pass_b.used ? "refined" : snippet.pass_b.ai_invoked ? "fallback_to_pass_a" : "skipped (no AI)")
                                : "skipped")}
                            </Badge>
                          </div>
                          {(snippet.pass_b.decision === "skipped" || snippet.pass_b.decision === "fallback_to_pass_a" || snippet.pass_b.decision === "internal_error" || (!snippet.pass_b.attempted && !snippet.pass_b.decision)) && (snippet.pass_b.skip_reason || snippet.pass_b.reason || snippet.pass_b.fail_reason) && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">{snippet.pass_b.decision === "skipped" ? "Skip reason:" : "Fallback reason:"}</span>{" "}
                              <span className="font-medium text-amber-600 dark:text-amber-400">{snippet.pass_b.skip_reason || snippet.pass_b.fail_reason || snippet.pass_b.reason}</span>
                            </div>
                          )}
                          {/* CR556: Structured gate snapshot — show evidence/height inputs when skipped */}
                          {snippet.pass_b.pass_b_gate && (
                            <div className="col-span-2 mt-1 p-2 rounded bg-muted/50 text-[10px] space-y-0.5">
                              <div className="font-medium text-xs text-muted-foreground">Gate Inputs</div>
                              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                                <div>
                                  <span className="text-muted-foreground">Evidence:</span>{" "}
                                  <span className="font-mono font-medium">
                                    {snippet.pass_b.pass_b_gate.inputs?.evidence_score_normalized != null
                                      ? `${(snippet.pass_b.pass_b_gate.inputs.evidence_score_normalized * 100).toFixed(0)}%`
                                      : "n/a"}
                                  </span>
                                  {snippet.pass_b.pass_b_gate.inputs?.min_evidence_threshold != null && (
                                    <span className="text-muted-foreground ml-1">
                                      (min {(snippet.pass_b.pass_b_gate.inputs.min_evidence_threshold * 100).toFixed(0)}%)
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Hits:</span>{" "}
                                  <span className="font-mono font-medium">{snippet.pass_b.pass_b_gate.inputs?.evidence_total_hits ?? "?"}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Height:</span>{" "}
                                  <span className="font-mono font-medium">
                                    {snippet.pass_b.pass_b_gate.inputs?.bbox_height_ratio != null
                                      ? snippet.pass_b.pass_b_gate.inputs.bbox_height_ratio.toFixed(4)
                                      : "n/a"}
                                  </span>
                                  {snippet.pass_b.pass_b_gate.inputs?.min_bbox_height_ratio != null && (
                                    <span className="text-muted-foreground ml-1">
                                      (min {snippet.pass_b.pass_b_gate.inputs.min_bbox_height_ratio.toFixed(4)})
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Candidate:</span>{" "}
                                  <span className="font-mono">{snippet.pass_b.pass_b_gate.inputs?.candidate_index ?? "?"}</span>
                                  {snippet.pass_b.pass_b_gate.inputs?.max_candidates_per_batch != null && (
                                    <span className="text-muted-foreground">/{snippet.pass_b.pass_b_gate.inputs.max_candidates_per_batch}</span>
                                  )}
                                  {snippet.pass_b.pass_b_gate.inputs?.in_crop_queue && (
                                    <Badge variant="outline" className="text-[8px] px-1 py-0 ml-1">CQ</Badge>
                                  )}
                                </div>
                              </div>
                              {snippet.pass_b.pass_b_gate.detail && (
                                <div className="text-muted-foreground mt-0.5">
                                  <span className="font-mono">{snippet.pass_b.pass_b_gate.detail}</span>
                                </div>
                              )}
                            </div>
                          )}
                          {snippet.pass_b.model_decision && (
                            <div>
                              <span className="text-muted-foreground">Model decision:</span>{" "}
                              <Badge
                                variant={snippet.pass_b.model_decision === "refine" ? "default" : "secondary"}
                                className="text-[10px] px-1 py-0"
                              >
                                {snippet.pass_b.model_decision}
                              </Badge>
                            </div>
                          )}
                          <div>
                            <span className="text-muted-foreground">Attempted:</span>{" "}
                            {/* CR610: Clarify attempted semantics — ROI created + intended to run */}
                            <span className="font-medium">{snippet.pass_b.attempted ? (snippet.pass_b.ai_invoked ? "Yes (AI called)" : "ROI prepared, AI skipped") : "No"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Used:</span>{" "}
                            <span className={`font-medium ${snippet.pass_b.used ? "text-primary" : "text-muted-foreground"}`}>
                              {snippet.pass_b.used ? "Yes ✓" : "No"}
                            </span>
                          </div>
                          {snippet.pass_b.confidence != null && (
                            <div>
                              <span className="text-muted-foreground">Confidence:</span>{" "}
                              <strong>{(snippet.pass_b.confidence * 100).toFixed(0)}%</strong>
                              {snippet.min_cropper_confidence_used != null && (
                                <span className="text-muted-foreground ml-1">
                                  (threshold: {(snippet.min_cropper_confidence_used * 100).toFixed(0)}%)
                                </span>
                              )}
                            </div>
                          )}
                          {/* CR555-P5: Override + fail reason badges */}
                          {snippet.pass_b_confidence_override_reason && (
                            <div className="col-span-2">
                              <Badge variant="outline" className="text-[9px] border-primary text-primary">
                                ⚡ {snippet.pass_b_confidence_override_reason}
                              </Badge>
                              {snippet.pass_b_roi_evidence_score != null && (
                                <span className="text-[10px] text-muted-foreground ml-1">
                                  (ROI evidence: {(snippet.pass_b_roi_evidence_score * 100).toFixed(0)}%)
                                </span>
                              )}
                            </div>
                          )}
                          {snippet.pass_b_fail_reason && !snippet.pass_b_confidence_override_reason && (
                            <div className="col-span-2">
                              <Badge variant="outline" className="text-[9px] border-destructive text-destructive">
                                {snippet.pass_b_fail_reason}
                              </Badge>
                            </div>
                          )}
                          {snippet.pass_b.parse_mode && (
                            <div>
                              <span className="text-muted-foreground">Parse mode:</span>{" "}
                              <Badge variant={snippet.pass_b.parse_mode === "direct" ? "default" : snippet.pass_b.parse_mode === "failed" ? "destructive" : "secondary"} className="text-[10px]">
                                {snippet.pass_b.parse_mode}
                              </Badge>
                            </div>
                          )}
                          {snippet.pass_b.error && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Error:</span>{" "}
                              <span className={`text-xs font-mono ${((snippet.snippet_status ?? "").toLowerCase() === "page_fallback") ? "text-amber-700 dark:text-amber-300" : "text-destructive"}`}>{snippet.pass_b.error}</span>
                            </div>
                          )}
                          {snippet.pass_b.reason && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Reason:</span>{" "}
                              <span className="text-xs">{snippet.pass_b.reason}</span>
                            </div>
                          )}
                          {snippet.pass_b.bbox_raw && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Raw bbox (pre-normalization):</span>{" "}
                              <span className="text-xs font-mono">{JSON.stringify(snippet.pass_b.bbox_raw)}</span>
                            </div>
                          )}
                          {/* CR553: Pass B input provenance */}
                          {snippet.pass_b.input_source && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Input source:</span>{" "}
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{snippet.pass_b.input_source}</Badge>
                              {snippet.pass_b.roi_px && (
                                <span className="text-[10px] text-muted-foreground ml-2">
                                  ROI: {snippet.pass_b.roi_px.w}×{snippet.pass_b.roi_px.h}px
                                </span>
                              )}
                            </div>
                          )}
                          {/* CR554: OCR availability indicator */}
                          {snippet.pass_b.ocr_available === false && (
                            <div className="col-span-2">
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                ⚠ OCR unavailable — image-only pass
                              </Badge>
                            </div>
                          )}
                          {/* CR591: Clamp diagnostics */}
                          {snippet.pass_b.clamped && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Clamped:</span>{" "}
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                Yes (overflow: {(snippet.pass_b.overflow_max ?? 0).toFixed(4)})
                              </Badge>
                            </div>
                          )}
                          {/* CR562: ROI-local clamp diagnostics */}
                          {snippet.pass_b.bbox_clamp_applied && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">ROI clamp:</span>{" "}
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                Applied (overflow: {(snippet.pass_b.bbox_max_overflow ?? 0).toFixed(4)})
                              </Badge>
                            </div>
                          )}
                          {snippet.pass_b.fail_reason && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Fail reason:</span>{" "}
                              <Badge
                                variant={((snippet.snippet_status ?? "").toLowerCase() === "page_fallback") ? "secondary" : "destructive"}
                                className={`text-[10px] px-1.5 py-0 ${((snippet.snippet_status ?? "").toLowerCase() === "page_fallback") ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" : ""}`}
                              >
                                {snippet.pass_b.fail_reason}
                              </Badge>
                            </div>
                          )}
                          {/* CR555: Anchor gate discrepancy */}
                          {snippet.pass_b.model_anchor_false_negative && (
                            <div className="col-span-2 p-1.5 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
                              <Badge className="text-[10px] px-1.5 py-0 bg-amber-500 text-white">
                                ⚠ model_anchor_false_negative
                              </Badge>
                              <span className="text-[10px] text-muted-foreground ml-1">
                                Model claimed "no anchors" but OCR proves they exist
                              </span>
                            </div>
                          )}
                          {snippet.pass_b.anchor_gate && (
                            <div className="col-span-2 text-[10px] space-y-0.5">
                              <div>
                                <span className="text-muted-foreground">OCR anchor gate:</span>{" "}
                                <Badge variant={snippet.pass_b.anchor_gate.ocr_anchor_passed ? "default" : "secondary"} className="text-[9px] px-1 py-0">
                                  {snippet.pass_b.anchor_gate.ocr_anchor_passed ? "passed ✓" : "failed"}
                                </Badge>
                              </div>
                              <div className="text-muted-foreground">
                                must: {snippet.pass_b.anchor_gate.must_hit_count}/{snippet.pass_b.anchor_gate.must_hit_total} ({((snippet.pass_b.anchor_gate.must_hit_ratio ?? 0) * 100).toFixed(0)}%)
                                {" · "}should: {snippet.pass_b.anchor_gate.should_hit_count}/{snippet.pass_b.anchor_gate.should_hit_total} ({((snippet.pass_b.anchor_gate.should_hit_ratio ?? 0) * 100).toFixed(0)}%)
                                {" · "}total hits: {snippet.pass_b.anchor_gate.anchor_token_hits ?? 0}
                              </div>
                            </div>
                          )}
                          {/* CR557: Input invariant guardrail warning */}
                          {snippet.pass_b.input_invariant && !snippet.pass_b.input_invariant.ok && (
                            <div className="col-span-2 p-1.5 rounded border border-destructive/50 bg-destructive/10 space-y-0.5">
                              <Badge className="text-[10px] px-1.5 py-0 bg-destructive text-destructive-foreground">
                                🚨 Pass B input invariant FAILED
                              </Badge>
                              <div className="text-[10px] text-destructive">
                                Failures: {snippet.pass_b.input_invariant.failures?.join(", ") ?? "unknown"}
                              </div>
                              {snippet.pass_b.input_invariant.areaRatio != null && (
                                <div className="text-[10px] text-muted-foreground">
                                  Input area ratio: {(snippet.pass_b.input_invariant.areaRatio * 100).toFixed(1)}% of page
                                </div>
                              )}
                            </div>
                          )}
                          {snippet.pass_b.input_invariant?.ok && (
                            <div className="col-span-2 text-[10px] text-muted-foreground">
                              Input invariant: <Badge variant="outline" className="text-[9px] px-1 py-0">✓ ok</Badge>
                              {snippet.pass_b.input_invariant.areaRatio != null && (
                                <span className="ml-1">({(snippet.pass_b.input_invariant.areaRatio * 100).toFixed(1)}% of page)</span>
                              )}
                            </div>
                          )}
                          {/* CR557: ROI source provenance */}
                          {snippet.pass_b.roi_source && (
                            <div className="col-span-2 text-[10px]">
                              <span className="text-muted-foreground">ROI source:</span>{" "}
                              <Badge variant="outline" className="text-[9px] px-1 py-0">
                                {snippet.pass_b.roi_source}
                              </Badge>
                            </div>
                          )}
                        </div>
                        {/* CR551B: Deep diagnostics panel */}
                        {snippet.pass_b.failure_stage && snippet.pass_b.failure_stage !== "none" && (
                          <div className="mt-2 p-2 rounded border border-destructive/30 bg-destructive/5 space-y-1 text-[10px]">
                            <div className="font-semibold text-destructive">Failure Diagnostics</div>
                            <div><span className="text-muted-foreground">Stage:</span> <Badge variant="destructive" className="text-[9px] px-1 py-0">{snippet.pass_b.failure_stage}</Badge></div>
                            {snippet.pass_b.failure_code && <div><span className="text-muted-foreground">Code:</span> <span className="font-mono font-medium">{snippet.pass_b.failure_code}</span></div>}
                            {snippet.pass_b.failure_detail && <div><span className="text-muted-foreground">Detail:</span> {snippet.pass_b.failure_detail}</div>}
                          </div>
                        )}
                        {snippet.pass_b.refinement_outcome && (
                          <div className="mt-1 text-[10px]">
                            <span className="text-muted-foreground">Refinement outcome:</span>{" "}
                            <Badge variant={snippet.pass_b.refinement_outcome === "refined_tighter" ? "default" : "secondary"} className="text-[9px] px-1 py-0">
                              {snippet.pass_b.refinement_outcome}
                            </Badge>
                          </div>
                        )}
                        {snippet.pass_b.roi_quality && (
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            ROI: {((snippet.pass_b.roi_quality.roi_area_ratio ?? 0) * 100).toFixed(1)}% of page · {snippet.pass_b.roi_quality.roi_text_char_count ?? 0} chars · density: {(snippet.pass_b.roi_quality.roi_text_density ?? 0).toFixed(2)}
                          </div>
                        )}
                        {snippet.pass_b.model_telemetry && (
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            Model: {snippet.pass_b.model_telemetry.model_name ?? "unknown"} · temp: {snippet.pass_b.model_telemetry.model_temperature ?? "?"} · in: {snippet.pass_b.model_telemetry.request_token_count ?? "?"} / out: {snippet.pass_b.model_telemetry.response_token_count ?? "?"}
                            {snippet.pass_b.model_telemetry.model_error_code && <span className="text-destructive ml-1">err: {snippet.pass_b.model_telemetry.model_error_code}</span>}
                          </div>
                        )}
                        {(snippet.pass_b.pass_a_ocr_sample || snippet.pass_b.pass_b_ocr_sample) && (
                          <div className="mt-1 space-y-0.5 text-[10px]">
                            {snippet.pass_b.pass_a_ocr_sample && <div><span className="text-muted-foreground">Pass A OCR:</span> <span className="font-mono text-[9px]">{snippet.pass_b.pass_a_ocr_sample.slice(0, 100)}…</span></div>}
                            {snippet.pass_b.pass_b_ocr_sample && <div><span className="text-muted-foreground">Pass B OCR:</span> <span className="font-mono text-[9px]">{snippet.pass_b.pass_b_ocr_sample.slice(0, 100)}…</span></div>}
                          </div>
                        )}
                        {/* CR553: Pass B ROI Input Preview */}
                        {snippet.pass_b.roi_asset_path && (
                          <div className="mt-2">
                            <button
                              type="button"
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                              onClick={() => setRoiPreviewOpen(!roiPreviewOpen)}
                            >
                              <ImageIcon className="h-3 w-3" />
                              {roiPreviewOpen ? 'Hide' : 'Show'} Pass B Input (ROI image)
                            </button>
                            {roiPreviewOpen && roiPreviewUrl && (
                              <div className="mt-2 border rounded-md overflow-hidden bg-muted/30">
                                <img
                                  src={roiPreviewUrl}
                                  alt="Pass B input ROI — what the cropper model actually evaluated"
                                  className="max-w-full max-h-[300px] object-contain mx-auto"
                                  draggable={false}
                                />
                                <div className="px-2 py-1 text-[10px] text-muted-foreground border-t bg-muted/20">
                                  This is the exact image Pass B (cropper) received. If this doesn't show the target deal panel, the ROI generation is wrong.
                                </div>
                              </div>
                            )}
                            {roiPreviewOpen && !roiPreviewUrl && (
                              <div className="mt-2 text-xs text-muted-foreground italic">
                                ROI image URL could not be loaded. Path: {snippet.pass_b.roi_asset_path}
                              </div>
                            )}
                          </div>
                        )}
                        {snippet.pass_b.matched_anchors && snippet.pass_b.matched_anchors.length > 0 && (
                          <div>
                            <span className="text-xs text-muted-foreground">Matched anchors:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {snippet.pass_b.matched_anchors.map((a, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">{a}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {snippet.pass_b.raw_preview && (
                          <details className="text-xs">
                            <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                              Raw model output (first ~400 chars)
                            </summary>
                            <pre className="mt-1 p-2 bg-muted rounded text-[10px] font-mono whitespace-pre-wrap break-all max-h-32 overflow-auto">
                              {snippet.pass_b.raw_preview}
                            </pre>
                          </details>
                        )}
                        {/* CR555-P4: ROI Evidence Diagnostics */}
                        {snippet.pass_b.roi_evidence && (
                          <div className="mt-2 p-2 border rounded bg-muted/20 space-y-1.5">
                            <div className="text-[10px] font-medium flex items-center gap-1">
                              <Layers className="h-3 w-3" />
                              ROI Evidence
                              <Badge
                                variant="outline"
                                className={`text-[9px] px-1 py-0 ml-1 ${
                                  (snippet.pass_b.roi_evidence.roi_evidence_score ?? 0) < 0.3
                                    ? "border-destructive text-destructive"
                                    : (snippet.pass_b.roi_evidence.roi_evidence_score ?? 0) < 0.6
                                    ? "border-amber-500 text-amber-600"
                                    : "border-primary text-primary"
                                }`}
                              >
                                {((snippet.pass_b.roi_evidence.roi_evidence_score ?? 0) * 100).toFixed(0)}%
                              </Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-1 text-[10px]">
                              <div>
                                <span className="text-muted-foreground">Brand:</span>{" "}
                                <span className="font-medium">{snippet.pass_b.roi_evidence.roi_evidence_hits_brand ?? 0}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Value:</span>{" "}
                                <span className="font-medium">{snippet.pass_b.roi_evidence.roi_evidence_hits_value ?? 0}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Mechanic:</span>{" "}
                                <span className="font-medium">{snippet.pass_b.roi_evidence.roi_evidence_hits_mechanic ?? 0}</span>
                              </div>
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              Total: {snippet.pass_b.roi_evidence.roi_evidence_total_hits ?? 0}/{snippet.pass_b.roi_evidence.roi_evidence_tokens_checked ?? 0} tokens
                              {" · "}{snippet.pass_b.roi_evidence.roi_ocr_char_count ?? 0} chars in ROI
                            </div>
                            {snippet.pass_b.roi_evidence.roi_matched_tokens && snippet.pass_b.roi_evidence.roi_matched_tokens.length > 0 && (
                              <div className="flex flex-wrap gap-0.5">
                                {snippet.pass_b.roi_evidence.roi_matched_tokens.map((t, i) => (
                                  <Badge key={i} variant="secondary" className="text-[9px] px-1 py-0">{t}</Badge>
                                ))}
                              </div>
                            )}
                            {snippet.pass_b.roi_evidence.roi_ocr_text_200 && (
                              <details className="text-[10px]">
                                <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                                  ROI OCR sample (first 200 chars)
                                </summary>
                                <pre className="mt-1 p-1.5 bg-muted rounded text-[9px] font-mono whitespace-pre-wrap break-all max-h-20 overflow-auto">
                                  {snippet.pass_b.roi_evidence.roi_ocr_text_200}
                                </pre>
                              </details>
                            )}
                          </div>
                    )}
                      </div>
                    )}

                    {/* CR554: Pass B Observatory — step-by-step trace timeline */}
                    {snippet.pass_b && (
                      <details className="mt-3 border rounded-md overflow-hidden">
                        <summary className="px-3 py-2 text-xs font-medium cursor-pointer hover:bg-muted/50 flex items-center gap-2">
                          <span>🔍 Pass B Trace</span>
                          <Badge
                            variant={snippet.pass_b.ai_invoked ? "default" : "outline"}
                            className="text-[9px] px-1.5 py-0"
                          >
                            {snippet.pass_b.ai_invoked ? "AI invoked ✓" : snippet.pass_b.attempted ? "AI skipped" : "Pass B skipped"}
                          </Badge>
                          {snippet.pass_b.decision && (
                            <Badge
                              variant={snippet.pass_b.decision === "refined" ? "default" : snippet.pass_b.decision === "internal_error" ? (((snippet.snippet_status ?? "").toLowerCase() === "page_fallback") ? "secondary" : "destructive") : "outline"}
                              className="text-[9px] px-1.5 py-0"
                            >
                              {snippet.pass_b.decision}
                            </Badge>
                          )}
                        </summary>
                        <div className="px-3 py-2 border-t bg-muted/10 space-y-2 text-[10px]">
                          {/* AI Summary Chip */}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            <div>
                              <span className="text-muted-foreground">AI invoked:</span>{" "}
                              <span className={`font-medium ${snippet.pass_b.ai_invoked ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`}>
                                {snippet.pass_b.ai_invoked ? "Yes" : "No — skipped"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Decision:</span>{" "}
                              <span className="font-medium">
                                {snippet.pass_b.pass_b_trace?.decision ?? snippet.pass_b.decision ?? "unknown"}
                              </span>
                            </div>
                            {(snippet.pass_b.pass_b_trace?.skip_reason || snippet.pass_b.ai_skip_reason) && (
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Skip reason:</span>{" "}
                                <span className="font-mono text-amber-600 dark:text-amber-400">{snippet.pass_b.pass_b_trace?.skip_reason || snippet.pass_b.ai_skip_reason}</span>
                              </div>
                            )}
                            {snippet.pass_b.pass_b_trace?.gate_reason && (
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Gate reason:</span>{" "}
                                <span className="font-mono text-amber-600 dark:text-amber-400">{snippet.pass_b.pass_b_trace.gate_reason}</span>
                              </div>
                            )}
                          </div>

                          {/* Stage Timeline */}
                          {snippet.pass_b.pass_b_trace?.stages && (
                            <div className="space-y-0.5 pt-1 border-t border-border/50">
                              <div className="font-medium text-muted-foreground mb-1">Stage timeline:</div>
                              {snippet.pass_b.pass_b_trace.stages.map((stage: { key: string; label: string; status: string; detail?: string }, idx: number) => {
                                const icon = stage.status === "success" ? "✅" : stage.status === "failed" ? "❌" : stage.status === "skipped" ? "⭘" : "⚪";
                                return (
                                  <div key={idx} className="flex items-start gap-1.5 group" title={stage.detail || undefined}>
                                    <span className="text-[11px] leading-none mt-0.5 flex-shrink-0">{icon}</span>
                                    <span className={`${stage.status === "failed" ? "text-destructive font-medium" : stage.status === "success" ? "" : "text-muted-foreground"}`}>
                                      {stage.label}
                                    </span>
                                    {stage.detail && (
                                      <span className="text-muted-foreground font-mono text-[9px] truncate max-w-[280px]" title={stage.detail}>
                                        — {stage.detail}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Fallback: old-style trace if no structured stages */}
                          {!snippet.pass_b.pass_b_trace?.stages && (
                            <div className="space-y-1 pt-1 border-t border-border/50">
                              {snippet.pass_b.ai_invoked && (
                                <div className="space-y-0.5">
                                  <div className="font-medium text-muted-foreground">Model proposal:</div>
                                  {snippet.pass_b.bbox_source_key && (
                                    <div className="text-muted-foreground">
                                      Source key: <span className="font-mono">{snippet.pass_b.bbox_source_key}</span>
                                      {snippet.pass_b.model_coord_space && (
                                        <> · coord_space: <span className="font-mono">{snippet.pass_b.model_coord_space}</span></>
                                      )}
                                    </div>
                                  )}
                                  {snippet.pass_b.bbox_norm ? (
                                    <div className="font-mono text-[9px]">
                                      x:{snippet.pass_b.bbox_norm.x.toFixed(4)} y:{snippet.pass_b.bbox_norm.y.toFixed(4)} w:{snippet.pass_b.bbox_norm.width.toFixed(4)} h:{snippet.pass_b.bbox_norm.height.toFixed(4)}
                                    </div>
                                  ) : (
                                    <div className="italic text-muted-foreground">No bbox returned</div>
                                  )}
                                </div>
                              )}
                              {(snippet.pass_b.failure_code || snippet.pass_b.fail_reason) && (
                                <div className="space-y-0.5">
                                  <div className="font-medium text-muted-foreground">Validation gates:</div>
                                  {snippet.pass_b.failure_stage && <div><span className="text-muted-foreground">Stage:</span> <Badge variant="destructive" className="text-[9px] px-1 py-0">{snippet.pass_b.failure_stage}</Badge></div>}
                                  {snippet.pass_b.failure_code && <div><span className="text-muted-foreground">Code:</span> <span className="font-mono font-medium">{snippet.pass_b.failure_code}</span></div>}
                                  {snippet.pass_b.failure_detail && <div className="text-muted-foreground italic">{snippet.pass_b.failure_detail}</div>}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Coords Sanity */}
                          {snippet.pass_b.pass_b_coords && (
                            <details className="pt-1 border-t border-border/50">
                              <summary className="text-muted-foreground cursor-pointer hover:text-foreground font-medium">Coords sanity</summary>
                              <div className="mt-1 space-y-0.5 font-mono text-[9px]">
                                {snippet.pass_b.pass_b_coords.proposal_bbox_norm && (
                                  <div>
                                    <span className="text-muted-foreground">Proposal (page):</span>{" "}
                                    x:{snippet.pass_b.pass_b_coords.proposal_bbox_norm.x.toFixed(4)} y:{snippet.pass_b.pass_b_coords.proposal_bbox_norm.y.toFixed(4)} w:{snippet.pass_b.pass_b_coords.proposal_bbox_norm.width.toFixed(4)} h:{snippet.pass_b.pass_b_coords.proposal_bbox_norm.height.toFixed(4)}
                                  </div>
                                )}
                                {snippet.pass_b.pass_b_coords.crop_bbox_norm && (
                                  <div>
                                    <span className="text-green-600 dark:text-green-400">Crop (page):</span>{" "}
                                    x:{snippet.pass_b.pass_b_coords.crop_bbox_norm.x.toFixed(4)} y:{snippet.pass_b.pass_b_coords.crop_bbox_norm.y.toFixed(4)} w:{snippet.pass_b.pass_b_coords.crop_bbox_norm.width.toFixed(4)} h:{snippet.pass_b.pass_b_coords.crop_bbox_norm.height.toFixed(4)}
                                  </div>
                                )}
                                <div>
                                  <span className="text-muted-foreground">Crop image source:</span>{" "}
                                  {snippet.pass_b.pass_b_coords.crop_image_source ?? "unknown"}
                                </div>
                                <div className="text-muted-foreground italic">All Pass B bboxes evaluated in PAGE space.</div>
                              </div>
                            </details>
                          )}

                          {/* Build stamp */}
                          {snippet.pass_b.build && (
                            <div className="pt-1 border-t border-border/50">
                              <span className="text-muted-foreground">Build:</span>{" "}
                              <span className="font-mono text-[9px]">
                                {snippet.pass_b.build.function_name ?? snippet.pass_b.build.function} @ {snippet.pass_b.build.version}
                                {snippet.pass_b.build.git_sha && snippet.pass_b.build.git_sha !== "local" && (
                                  <> · <span className="text-muted-foreground">sha:</span>{snippet.pass_b.build.git_sha.slice(0, 12)}</>
                                )}
                              </span>
                            </div>
                          )}
                          {/* ROI expansion diagnostics */}
                          {(snippet.pass_b.roi_expansion_mode || snippet.pass_b.roi_margin_used != null) && (
                            <div className="pt-1 border-t border-border/50 text-muted-foreground">
                              <span>ROI:</span>{" "}
                              <span className="font-mono">
                                mode={snippet.pass_b.roi_expansion_mode ?? "symmetric"}
                                {snippet.pass_b.roi_margin_used != null && <> margin={snippet.pass_b.roi_margin_used.toFixed(3)}</>}
                                {snippet.pass_b.roi_area_ratio != null && <> area={snippet.pass_b.roi_area_ratio.toFixed(3)}</>}
                              </span>
                            </div>
                          )}

                          {/* Bbox Audit Trail */}
                          {snippet.bbox_audit_trail && (
                            <div className="pt-1 border-t border-border/50 space-y-0.5">
                              <div className="font-medium text-muted-foreground">Bbox audit trail:</div>
                              <div className="grid grid-cols-1 gap-0.5 font-mono text-[9px]">
                                {snippet.bbox_audit_trail.pass_a_bbox_norm && (
                                  <div><span className="text-muted-foreground">Pass A:</span> x:{snippet.bbox_audit_trail.pass_a_bbox_norm.x.toFixed(4)} y:{snippet.bbox_audit_trail.pass_a_bbox_norm.y.toFixed(4)} w:{snippet.bbox_audit_trail.pass_a_bbox_norm.width.toFixed(4)} h:{snippet.bbox_audit_trail.pass_a_bbox_norm.height.toFixed(4)}</div>
                                )}
                                {snippet.bbox_audit_trail.input_bbox_norm && (
                                  <div><span className="text-muted-foreground">Input (to B):</span> x:{snippet.bbox_audit_trail.input_bbox_norm.x.toFixed(4)} y:{snippet.bbox_audit_trail.input_bbox_norm.y.toFixed(4)} w:{snippet.bbox_audit_trail.input_bbox_norm.width.toFixed(4)} h:{snippet.bbox_audit_trail.input_bbox_norm.height.toFixed(4)}</div>
                                )}
                                {snippet.bbox_audit_trail.model_bbox_norm && (
                                  <div><span className="text-muted-foreground">Model proposed:</span> x:{snippet.bbox_audit_trail.model_bbox_norm.x.toFixed(4)} y:{snippet.bbox_audit_trail.model_bbox_norm.y.toFixed(4)} w:{snippet.bbox_audit_trail.model_bbox_norm.width.toFixed(4)} h:{snippet.bbox_audit_trail.model_bbox_norm.height.toFixed(4)}</div>
                                )}
                                {snippet.bbox_audit_trail.evaluated_bbox_norm && (
                                  <div>
                                    <span className="text-amber-600 dark:text-amber-400">Gate evaluated:</span> x:{snippet.bbox_audit_trail.evaluated_bbox_norm.x.toFixed(4)} y:{snippet.bbox_audit_trail.evaluated_bbox_norm.y.toFixed(4)} w:{snippet.bbox_audit_trail.evaluated_bbox_norm.width.toFixed(4)} h:{snippet.bbox_audit_trail.evaluated_bbox_norm.height.toFixed(4)}
                                    {snippet.bbox_audit_trail.model_bbox_norm && snippet.bbox_audit_trail.evaluated_bbox_norm &&
                                      (snippet.bbox_audit_trail.model_bbox_norm.x !== snippet.bbox_audit_trail.evaluated_bbox_norm.x ||
                                       snippet.bbox_audit_trail.model_bbox_norm.y !== snippet.bbox_audit_trail.evaluated_bbox_norm.y) && (
                                      <Badge variant="destructive" className="ml-1 text-[8px] px-1 py-0">BBOX MISMATCH</Badge>
                                    )}
                                  </div>
                                )}
                                {snippet.bbox_audit_trail.final_bbox_norm && (
                                  <div><span className="text-green-600 dark:text-green-400">Final used:</span> x:{snippet.bbox_audit_trail.final_bbox_norm.x.toFixed(4)} y:{snippet.bbox_audit_trail.final_bbox_norm.y.toFixed(4)} w:{snippet.bbox_audit_trail.final_bbox_norm.width.toFixed(4)} h:{snippet.bbox_audit_trail.final_bbox_norm.height.toFixed(4)} <span className="text-muted-foreground">({snippet.bbox_audit_trail.final_source})</span></div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </details>
                    )}

                    {snippet.pass_b_regression?.allowed_despite_regression && (
                      <div className="mt-2 p-2 border border-amber-400/50 rounded bg-amber-50/30 dark:bg-amber-950/20">
                        <Badge variant="warning" className="text-[10px]">
                          ⚠ Allowed despite regression (subset + high confidence)
                        </Badge>
                        <div className="mt-1 grid grid-cols-2 gap-1 text-[10px] text-muted-foreground">
                          <div>Score A: {((snippet.pass_b_regression.score_a ?? 0) * 100).toFixed(1)}%</div>
                          <div>Score B: {((snippet.pass_b_regression.score_b ?? 0) * 100).toFixed(1)}%</div>
                          <div>Conf B: {((snippet.pass_b_regression.conf_b ?? 0) * 100).toFixed(0)}%</div>
                          <div>Subset: {snippet.pass_b_regression.subset_of_a ? "yes" : "no"}</div>
                        </div>
                      </div>
                    )}

                    {/* CR557: ROI OCR Text (always visible, even when Pass B bbox is null) */}
                    {snippet.pass_b?.roi_ocr_text_200 && !snippet.pass_b_ocr_text_200 && (
                      <div className="mt-2 p-2 border rounded bg-muted/20 space-y-1">
                        <div className="text-[10px] font-medium">
                          ROI OCR ({snippet.pass_b.roi_ocr_source ?? "S1_2_PAGE_OCR"})
                        </div>
                        <pre className="p-1.5 bg-muted rounded text-[9px] font-mono whitespace-pre-wrap break-all max-h-24 overflow-auto">
                          {snippet.pass_b.roi_ocr_text_200}
                        </pre>
                      </div>
                    )}

                    {/* CR556: Pass B OCR Text */}
                    {snippet.pass_b_ocr_text_200 && (
                      <div className="mt-2 p-2 border rounded bg-muted/20 space-y-1">
                        <div className="text-[10px] font-medium">
                          Pass B OCR ({snippet.pass_b_ocr_source ?? "unknown"})
                          {snippet.pass_b_ocr_hit_count != null && (
                            <span className="text-muted-foreground ml-1">· {snippet.pass_b_ocr_hit_count} words</span>
                          )}
                        </div>
                        <pre className="p-1.5 bg-muted rounded text-[9px] font-mono whitespace-pre-wrap break-all max-h-24 overflow-auto">
                          {snippet.pass_b_ocr_text_200}
                        </pre>
                      </div>
                    )}
                    {snippet.evidence_score != null && (
                      <div className="mt-3 p-3 border rounded-md bg-muted/30 space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium">
                          <Layers className="h-3 w-3" />
                          Evidence — "What's in the Box?"
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Evidence score:</span>{" "}
                            <strong className={snippet.evidence_score < 0.3 ? "text-destructive" : snippet.evidence_score < 0.6 ? "text-amber-600" : "text-primary"}>
                              {(snippet.evidence_score * 100).toFixed(0)}%
                            </strong>
                          </div>
                          {snippet.confidence_final != null && (
                            <div>
                              <span className="text-muted-foreground">Calibrated confidence:</span>{" "}
                              <strong className={snippet.confidence_final < 0.5 ? "text-destructive" : snippet.confidence_final < 0.7 ? "text-amber-600" : "text-primary"}>
                                {(snippet.confidence_final * 100).toFixed(0)}%
                              </strong>
                            </div>
                          )}
                          {snippet.evidence_hits && (
                            <>
                              <div>
                                <span className="text-muted-foreground">Brand hits:</span>{" "}
                                <strong>{snippet.evidence_hits.brand ?? 0}</strong>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Value hits:</span>{" "}
                                <strong>{snippet.evidence_hits.value ?? 0}</strong>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Mechanic hits:</span>{" "}
                                <strong>{snippet.evidence_hits.mechanic ?? 0}</strong>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Tokens checked:</span>{" "}
                                <span>{snippet.evidence_hits.tokens_checked ?? 0}</span>
                              </div>
                            </>
                          )}
                          {snippet.evidence_pass_a && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Pass A evidence:</span>{" "}
                              <span>{((snippet.evidence_pass_a.score ?? 0) * 100).toFixed(0)}% ({snippet.evidence_pass_a.hits ?? 0} hits)</span>
                            </div>
                          )}
                        </div>
                        {snippet.matched_evidence_tokens && snippet.matched_evidence_tokens.length > 0 && (
                          <div>
                            <span className="text-xs text-muted-foreground">Matched evidence tokens:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {snippet.matched_evidence_tokens.map((t, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">{t}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* CR574: Box OCR Evidence panel */}
                    {snippet.box_ocr_text_200 && (
                      <div className="mt-3 p-3 border rounded-md bg-muted/30 space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium">
                          <Target className="h-3 w-3" />
                          Box OCR — What's Actually Inside
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {snippet.box_must_hit_count != null && (
                            <div>
                              <span className="text-muted-foreground">Must-hit in box:</span>{" "}
                              <strong>{snippet.box_must_hit_count}</strong>
                              {snippet.must_hit_tokens_used && (
                                <span className="text-muted-foreground ml-1">/ {snippet.must_hit_tokens_used.length}</span>
                              )}
                            </div>
                          )}
                          {snippet.box_should_hit_count != null && (
                            <div>
                              <span className="text-muted-foreground">Should-hit in box:</span>{" "}
                              <strong>{snippet.box_should_hit_count}</strong>
                              {snippet.should_hit_tokens_used && (
                                <span className="text-muted-foreground ml-1">/ {snippet.should_hit_tokens_used.length}</span>
                              )}
                            </div>
                          )}
                        </div>
                        {snippet.box_anchor_hits && snippet.box_anchor_hits.length > 0 && (
                          <div>
                            <span className="text-xs text-muted-foreground">Anchor hits in box:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {snippet.box_anchor_hits.map((t, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">{t}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <details className="text-xs">
                          <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                            Box OCR sample (first 200 chars)
                          </summary>
                          <pre className="mt-1 p-2 bg-muted rounded text-[10px] font-mono whitespace-pre-wrap break-all max-h-32 overflow-auto">
                            {snippet.box_ocr_text_200}
                          </pre>
                        </details>
                      </div>
                    )}

                    {/* CR587: Anchor Suggestions panel */}
                    {snippet.anchor_suggestions && (
                      <div className="mt-3 p-3 border rounded-md bg-muted/30 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs font-medium">
                            <Layers className="h-3 w-3" />
                            Anchor Suggestions
                            <span className="text-[10px] text-muted-foreground font-normal">CR587</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 text-[10px] px-1.5 gap-1"
                            onClick={() => {
                              const blob = {
                                keep_must: snippet.anchor_suggestions?.keep_must ?? [],
                                keep_should: snippet.anchor_suggestions?.keep_should ?? [],
                                demote_must: snippet.anchor_suggestions?.demote_must ?? [],
                                new_from_box: snippet.anchor_suggestions?.new_from_box ?? [],
                              };
                              navigator.clipboard.writeText(JSON.stringify(blob, null, 2));
                              toast({ title: "Copied", description: "Anchor suggestions copied to clipboard" });
                            }}
                          >
                            📋 Copy for prompt pack
                          </Button>
                        </div>

                        <div className="text-xs text-muted-foreground space-y-0.5">
                          <div>Current must-hit: {snippet.must_hit_tokens_used?.length ? snippet.must_hit_tokens_used.join(', ') : '—'}</div>
                          <div>Current should-hit: {snippet.should_hit_tokens_used?.length ? snippet.should_hit_tokens_used.join(', ') : '—'}</div>
                        </div>

                        <div className="space-y-2 text-xs">
                          {snippet.anchor_suggestions.keep_must && snippet.anchor_suggestions.keep_must.length > 0 && (
                            <div>
                              <div className="font-medium text-muted-foreground">✅ Keep as must-hit</div>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {snippet.anchor_suggestions.keep_must.map((t: string) => (
                                  <Badge key={t} variant="outline" className="text-[9px] px-1 py-0 border-emerald-500/40 text-emerald-700 dark:text-emerald-400">{t}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {snippet.anchor_suggestions.keep_should && snippet.anchor_suggestions.keep_should.length > 0 && (
                            <div>
                              <div className="font-medium text-muted-foreground">✅ Keep as should-hit</div>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {snippet.anchor_suggestions.keep_should.map((t: string) => (
                                  <Badge key={t} variant="outline" className="text-[9px] px-1 py-0 border-emerald-500/40 text-emerald-700 dark:text-emerald-400">{t}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {snippet.anchor_suggestions.demote_must && snippet.anchor_suggestions.demote_must.length > 0 && (
                            <div>
                              <div className="font-medium text-muted-foreground">⚠️ Consider demoting / removing</div>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {snippet.anchor_suggestions.demote_must.map((t: string) => (
                                  <Badge key={t} variant="outline" className="text-[9px] px-1 py-0 border-amber-500/40 text-amber-700 dark:text-amber-400">{t}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {snippet.anchor_suggestions.new_from_box && snippet.anchor_suggestions.new_from_box.length > 0 && (
                            <div>
                              <div className="font-medium text-muted-foreground">💡 New candidates from box OCR</div>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {snippet.anchor_suggestions.new_from_box.map((t: string) => (
                                  <Badge key={t} variant="outline" className="text-[9px] px-1 py-0 border-sky-500/40 text-sky-700 dark:text-sky-400">{t}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {snippet.anchor_suggestions.debug_sample?.box_ocr_text_200 && (
                          <details className="text-xs">
                            <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                              Box OCR sample
                            </summary>
                            <pre className="mt-1 p-2 bg-muted rounded text-[10px] font-mono whitespace-pre-wrap break-all max-h-32 overflow-auto">
                              {snippet.anchor_suggestions.debug_sample.box_ocr_text_200}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-sm">
                    No source page image available.
                  </div>
                )}
              </TabsContent>

              <TabsContent value="cropped" className="mt-3">
                {snippetUrl ? (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Cropped snippet image</p>
                    <div className="border rounded-md overflow-hidden bg-muted/30 p-2">
                      <img
                        src={snippetUrl}
                        alt="Cropped snippet"
                        className="block max-w-full mx-auto"
                        draggable={false}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-sm">
                    <Crop className="h-6 w-6 mx-auto mb-2 opacity-50" />
                    <p>No cropped snippet yet.</p>
                    <p className="text-xs mt-1">
                      {snippet.bbox_norm
                        ? "Bbox available — auto-crop should produce the snippet asset."
                        : "No bbox available — snippet cannot be cropped."}
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StatusChip({ status }: { status: string | null }) {
  if (!status) return null;
  const s = status.toLowerCase();
  const isGood = ["locator_ok", "locator_heuristic_bbox", "located", "cropped", "uploaded", "accepted"].includes(s);
  const isSoft = s === "locator_ok_anchor_soft";
  const isSoftFallback = s === "locator_ok_soft_fallback";
  const isTruth = s === "locator_truth_override";
  const isFallbackCropped = s === "fallback_cropped";
  const isBad = s.startsWith("rejected") || s.startsWith("locator_bbox") || s === "locator_model_failed";
  return (
    <Badge
      variant={(isGood || isTruth) ? "default" : isFallbackCropped ? "secondary" : (isSoft || isSoftFallback) ? "outline" : isBad ? "destructive" : "secondary"}
      className={`text-xs ${isTruth ? 'bg-emerald-600' : isFallbackCropped ? 'bg-amber-500/80 text-amber-950 dark:bg-amber-600/80 dark:text-amber-50' : isSoft ? 'border-teal-500/50 text-teal-700 dark:text-teal-400' : isSoftFallback ? 'border-cyan-500/50 text-cyan-700 dark:text-cyan-400' : ''}`}
    >
      {isTruth ? '🔒 truth override' : isFallbackCropped ? '⚠ fallback snippet' : isSoft ? 'ok (soft gate)' : isSoftFallback ? 'ok (ghost fallback)' : status}
    </Badge>
  );
}
